import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  Phone, Mail, CreditCard, Zap, AudioLines,
  CheckCircle, XCircle, AlertTriangle, Loader2,
  ExternalLink, Trash2, TestTube, ArrowRight, ArrowLeft,
  Shield, Plug, ChevronRight, RefreshCw, KeyRound, Calendar, Link2, Loader2 as Spinner,
  Copy, Globe, MessageCircle, Search, MapPin, Wifi, PhoneOutgoing, Wallet, BadgeCheck, Radio
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api, { voiceAPI, cadencesAPI } from '../lib/api';
import MailHub from '../components/integrations/MailHub';
import { HelpDot } from '../components/HelpDot';
import ConnectMailWizard from '../components/integrations/ConnectMailWizard';

// Email providers are managed by the unified "Email & Inbox" hub, not the grid.
const EMAIL_PROVIDER_IDS = ['sendgrid', 'smtp', 'gmail', 'imap', 'mailgun'];
const PROVIDER_CATEGORY = {
  twilio: 'voice', exotel: 'voice', vonage: 'voice', plivo: 'voice', elevenlabs: 'voice',
  stripe: 'payments', razorpay: 'payments', paypal: 'payments',
  zapier: 'automation', make: 'automation', webhook: 'automation',
};

function CollapsibleSection({ title, count, children, defaultOpen = true }) {
  const [open, setOpen] = React.useState(defaultOpen);
  const slug = title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
  return (
    <div className="mb-4 rounded-xl border border-border bg-card/40 overflow-hidden" data-testid={`section-${slug}`}>
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid={`section-toggle-${slug}`}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-muted/40 transition-colors"
      >
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold">{title}</span>
          {typeof count === 'number' && <Badge variant="outline" className="text-[10px]">{count}</Badge>}
        </div>
        <ChevronRight className={`h-4 w-4 text-muted-foreground transition-transform ${open ? 'rotate-90' : ''}`} />
      </button>
      {open && (
        <div className="px-4 pb-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {children}
        </div>
      )}
    </div>
  );
}

const PROVIDER_ICONS = {
  twilio: Phone,
  exotel: Phone,
  elevenlabs: AudioLines,
  sendgrid: Mail,
  stripe: CreditCard,
  razorpay: CreditCard,
  zapier: Zap,
  smtp: Mail,
};

const PROVIDER_COLORS = {
  twilio: 'text-red-500 bg-red-500/10',
  exotel: 'text-emerald-500 bg-emerald-500/10',
  elevenlabs: 'text-violet-500 bg-violet-500/10',
  sendgrid: 'text-blue-500 bg-blue-500/10',
  stripe: 'text-indigo-500 bg-indigo-500/10',
  razorpay: 'text-sky-500 bg-sky-500/10',
  zapier: 'text-orange-500 bg-orange-500/10',
  smtp: 'text-teal-500 bg-teal-500/10',
};

const PROVIDER_HELP = {
  twilio: {
    steps: [
      'Log into your Twilio Console at console.twilio.com',
      'Copy your Account SID from the dashboard',
      'Copy your Auth Token (click "Show" to reveal it)',
      'Go to Phone Numbers > Manage > Active Numbers and copy your Twilio phone number',
    ],
    fields: ['account_sid', 'api_secret', 'phone_number'],
    labels: { account_sid: 'Account SID', api_secret: 'Auth Token', phone_number: 'Twilio Phone Number' },
    placeholders: { account_sid: 'AC...', api_secret: 'Your Auth Token', phone_number: '+1234567890' },
  },
  elevenlabs: {
    steps: [],
    fields: [],
    labels: {},
    placeholders: {},
    _hidden: true,
  },
  exotel: {
    steps: [
      'Log into your Exotel dashboard at my.exotel.com',
      'Go to Settings > API Settings: copy your Account SID (subdomain, e.g. lockene3)',
      'Copy your API Key and API Token (used together for authentication)',
      'Copy your ExoPhone (the virtual caller-ID number, e.g. 08047285505)',
      'Go to App Bazaar > your Voice applet and copy its App ID — REQUIRED for AI calling',
      'Optional: paste your ElevenLabs Exotel phone id for native AI voice. If left blank we auto-register your ExoPhone.',
    ],
    fields: ['account_sid', 'api_key', 'api_secret', 'phone_number', 'applet_id', 'region', 'elevenlabs_phone_id'],
    optional: ['region', 'elevenlabs_phone_id'],
    labels: {
      account_sid: 'Exotel SID',
      api_key: 'API Key',
      api_secret: 'API Token',
      phone_number: 'ExoPhone (Caller ID)',
      region: 'Exotel API Cluster (optional)',
      applet_id: 'Applet ID (App ID — required for AI calls)',
      elevenlabs_phone_id: 'ElevenLabs Phone ID (optional)',
    },
    placeholders: {
      account_sid: 'lockene3',
      api_key: 'Your Exotel API Key',
      api_secret: 'Your Exotel API Token',
      phone_number: '08047285505',
      region: 'api.exotel.com',
      applet_id: '1266493',
      elevenlabs_phone_id: 'phnum_...',
    },
  },
  sendgrid: {
    steps: [
      'Go to app.sendgrid.com and sign in',
      'Navigate to Settings > API Keys',
      'Click "Create API Key" with Full Access or Mail Send permissions',
      'Copy the generated key (it is shown only once!)',
    ],
    fields: ['api_key', 'sender_email'],
    labels: { api_key: 'API Key', sender_email: 'Verified Sender Email' },
    placeholders: { api_key: 'SG...', sender_email: 'you@yourdomain.com (must be a Verified Sender in SendGrid)' },
  },
  stripe: {
    steps: [
      'Go to dashboard.stripe.com and sign in',
      'Navigate to Developers > API Keys',
      'Copy your Secret key (starts with sk_test_ or sk_live_)',
    ],
    fields: ['api_key'],
    labels: { api_key: 'Secret Key' },
    placeholders: { api_key: 'sk_test_...' },
  },
  razorpay: {
    steps: [
      'Go to dashboard.razorpay.com and sign in',
      'Navigate to Account & Settings > API Keys',
      'Generate (or regenerate) your Key — copy both Key ID and Key Secret',
    ],
    fields: ['api_key', 'api_secret'],
    labels: { api_key: 'Key ID', api_secret: 'Key Secret' },
    placeholders: { api_key: 'rzp_live_... or rzp_test_...', api_secret: 'Your Razorpay Key Secret' },
  },
  zapier: {
    steps: [
      'Go to zapier.com and sign in',
      'Navigate to Developer Platform or use your Zapier webhook URL',
      'Generate an API key from your account settings',
    ],
    fields: ['api_key'],
    labels: { api_key: 'API Key' },
    placeholders: { api_key: 'Your Zapier API key' },
  },
  smtp: {
    steps: [
      'Get your SMTP server details from your email provider',
      'For Gmail: smtp.gmail.com, Port 465 (SSL). Use an App Password from myaccount.google.com/apppasswords',
      'For Outlook: smtp.office365.com, Port 587 (TLS)',
      'For custom domains: Check your hosting provider for SMTP settings',
    ],
    fields: ['smtp_host', 'smtp_port', 'smtp_username', 'smtp_password', 'smtp_from_email', 'smtp_from_name', 'imap_host', 'imap_port'],
    optional: ['smtp_from_name', 'imap_host', 'imap_port'],
    labels: {
      smtp_host: 'SMTP Server',
      smtp_port: 'Port',
      smtp_username: 'Username / Email',
      smtp_password: 'Password / App Password',
      smtp_from_email: 'From Email Address',
      smtp_from_name: 'From Display Name (optional)',
      imap_host: 'IMAP Server (optional — for inbound reply capture)',
      imap_port: 'IMAP Port (optional, default 993)',
    },
    placeholders: {
      smtp_host: 'smtp.gmail.com',
      smtp_port: '465',
      smtp_username: 'you@company.com',
      smtp_password: 'App password or SMTP password',
      smtp_from_email: 'you@company.com',
      smtp_from_name: 'Your Name',
      imap_host: 'Auto-derived from your SMTP host — match your data center (.in vs .com)',
      imap_port: '993',
    },
  },
};

// ─── Integration Card ───
function IntegrationCard({ integration, provider, onSetup, onTest, onSendTest, onDisconnect, testing }) {
  const Icon = PROVIDER_ICONS[provider.id] || Plug;
  const colors = PROVIDER_COLORS[provider.id] || 'text-gray-500 bg-gray-500/10';
  const isConfigured = integration?.is_configured;
  const isValid = integration?.validation_status === 'valid';
  const isEmailProvider = provider.id === 'smtp' || provider.id === 'sendgrid';

  return (
    <Card
      className={`transition-all hover:shadow-md ${isConfigured ? 'border-primary/20' : 'border-dashed'}`}
      data-testid={`integration-card-${provider.id}`}
    >
      <CardContent className="pt-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${colors}`}>
              <Icon className="h-5 w-5" />
            </div>
            <div>
              <h3 className="font-semibold text-base">{provider.name}</h3>
              <p className="text-xs text-muted-foreground">{provider.description}</p>
            </div>
          </div>
          {isConfigured ? (
            isValid ? (
              <Badge variant="outline" className="bg-green-500/5 border-green-500/30 text-green-600" data-testid={`status-${provider.id}`}>
                <CheckCircle className="h-3 w-3 mr-1" />Connected
              </Badge>
            ) : integration?.validation_status === 'invalid' ? (
              <Badge variant="destructive" data-testid={`status-${provider.id}`}>
                <XCircle className="h-3 w-3 mr-1" />Invalid
              </Badge>
            ) : (
              <Badge variant="secondary" data-testid={`status-${provider.id}`}>
                <AlertTriangle className="h-3 w-3 mr-1" />Untested
              </Badge>
            )
          ) : (
            <Badge variant="outline" className="text-muted-foreground" data-testid={`status-${provider.id}`}>
              Not Connected
            </Badge>
          )}
        </div>

        {isConfigured && (
          <div className="mb-4 p-2 rounded bg-muted/30 border border-border/50">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
              <span className="flex items-center gap-1.5 min-w-0 max-w-full">
                <KeyRound className="h-3 w-3 shrink-0" />
                <span className="truncate" title={integration.key_prefix || integration.account_sid_prefix || '****'}>
                  Key: {integration.key_prefix || integration.account_sid_prefix || '****'}
                </span>
              </span>
              {integration.phone_number && (
                <span className="shrink-0 whitespace-nowrap">Phone: {integration.phone_number}</span>
              )}
              {integration.last_validated && (
                <span className="shrink-0 whitespace-nowrap sm:ml-auto">
                  Last tested: {new Date(integration.last_validated).toLocaleDateString()}
                </span>
              )}
            </div>
            {(provider.id === 'exotel' || provider.id === 'twilio') && (
              <div className="mt-2 pt-2 border-t border-border/40">
                {integration.voice_ai_status === 'ready' || integration.elevenlabs_phone_id ? (
                  <Badge variant="outline" className="bg-green-500/5 border-green-500/30 text-green-600 text-[11px]" data-testid={`voice-ai-ready-${provider.id}`}>
                    <CheckCircle className="h-3 w-3 mr-1" />Voice AI ready
                  </Badge>
                ) : integration.voice_ai_status === 'registration_failed' ? (
                  <Badge variant="outline" className="bg-amber-500/5 border-amber-500/30 text-amber-600 text-[11px]" data-testid={`voice-ai-ready-${provider.id}`}>
                    <AlertTriangle className="h-3 w-3 mr-1" />Connected, but AI calling not wired — run Test to retry
                  </Badge>
                ) : integration.voice_ai_status === 'needs_applet_id' || (provider.id === 'exotel' && !integration.applet_id) ? (
                  <Badge variant="outline" className="bg-amber-500/5 border-amber-500/30 text-amber-600 text-[11px]" data-testid={`voice-ai-ready-${provider.id}`}>
                    <AlertTriangle className="h-3 w-3 mr-1" />Voice AI pending — add your Exotel Applet ID (Manage)
                  </Badge>
                ) : (
                  <Badge variant="outline" className="text-muted-foreground text-[11px]" data-testid={`voice-ai-ready-${provider.id}`}>
                    <AlertTriangle className="h-3 w-3 mr-1" />Voice AI pending — click Test to wire AI calling
                  </Badge>
                )}
              </div>
            )}
            {provider.id === 'smtp' && (
              <div className="mt-2 pt-2 border-t border-border/40" data-testid="imap-status-row">
                {integration.imap_status === 'connected' ? (
                  <Badge variant="outline" className="bg-green-500/5 border-green-500/30 text-green-600 text-[11px]" data-testid={`imap-status-${provider.id}`}>
                    <CheckCircle className="h-3 w-3 mr-1" />Inbound replies: connected
                    {integration.imap_host ? ` (${integration.imap_host})` : ''}
                  </Badge>
                ) : integration.imap_status === 'needs_attention' ? (
                  (() => {
                    const err = (integration.imap_last_error || '').toLowerCase();
                    const isDns = err.includes('name or service not known') || err.includes('getaddrinfo')
                      || err.includes('nodename nor servname') || err.includes('name resolution') || err.includes('-2]');
                    return (
                      <div className="flex flex-col gap-1 rounded-md border border-amber-500/30 bg-amber-500/5 px-2.5 py-2" data-testid="imap-dns-banner">
                        <Badge variant="outline" className="bg-transparent border-amber-500/40 text-amber-600 text-[11px] w-fit" data-testid={`imap-status-${provider.id}`}>
                          <AlertTriangle className="h-3 w-3 mr-1" />
                          {isDns ? 'Inbound replies: IMAP host not found' : 'Inbound replies: needs attention'}
                        </Badge>
                        <p className="text-[11px] text-amber-700/90">
                          {isDns
                            ? <>The IMAP server <span className="font-mono font-medium">{integration.imap_host || '(unset)'}</span> couldn't be reached (DNS lookup failed). Double-check the server address and make sure it matches your data center (e.g. <span className="font-mono">.in</span> for India accounts, <span className="font-mono">.com</span> otherwise), then press Update.</>
                            : <>Couldn't connect to IMAP — update your app password or set the IMAP host, then press Update.</>}
                        </p>
                        {integration.imap_last_error && (
                          <p className="text-[10px] text-amber-600/70 font-mono truncate" title={integration.imap_last_error}>
                            {integration.imap_last_error}
                          </p>
                        )}
                      </div>
                    );
                  })()
                ) : (
                  <Badge variant="outline" className="text-muted-foreground text-[11px]" data-testid={`imap-status-${provider.id}`}>
                    <AlertTriangle className="h-3 w-3 mr-1" />Inbound replies: not yet polled
                  </Badge>
                )}
              </div>
            )}
          </div>
        )}

        <div className="flex gap-2">
          <Button
            variant={isConfigured ? 'outline' : 'default'}
            size="sm"
            className="flex-1"
            onClick={() => onSetup(provider.id)}
            data-testid={`setup-${provider.id}-btn`}
          >
            {isConfigured ? 'Update' : 'Connect'}
            <ChevronRight className="h-3 w-3 ml-1" />
          </Button>
          {isConfigured && (
            <>
              <Button
                variant="outline"
                size="sm"
                disabled={testing === provider.id}
                onClick={() => onTest(provider.id)}
                data-testid={`test-${provider.id}-btn`}
              >
                {testing === provider.id ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  <TestTube className="h-3 w-3" />
                )}
              </Button>
              {isEmailProvider && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onSendTest(provider.id)}
                  data-testid={`send-test-email-${provider.id}-btn`}
                  title="Send a test email"
                >
                  <Mail className="h-3 w-3 mr-1" /> Send test
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                className="text-red-500 hover:text-red-600 hover:bg-red-500/10"
                onClick={() => onDisconnect(provider.id)}
                data-testid={`disconnect-${provider.id}-btn`}
              >
                <Trash2 className="h-3 w-3" />
              </Button>
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Setup Wizard Dialog ───
function SetupWizard({ provider, open, onClose, onSave, saving, initialPool }) {
  const help = PROVIDER_HELP[provider] || { steps: [], fields: ['api_key'], labels: { api_key: 'API Key' }, placeholders: {} };
  const [fields, setFields] = useState({});
  const [step, setStep] = useState(0); // 0 = instructions, 1 = credentials

  useEffect(() => {
    if (open) {
      const pool = initialPool && typeof initialPool === 'object'
        ? Object.entries(initialPool).map(([country, number]) => ({ country, number }))
        : [];
      setFields(pool.length ? { _caller_pool: pool } : {});
      setStep(0);
    }
  }, [open, provider]);

  const providerInfo = PROVIDER_ICONS[provider] ? {
    name: provider.charAt(0).toUpperCase() + provider.slice(1),
    Icon: PROVIDER_ICONS[provider],
    colors: PROVIDER_COLORS[provider],
  } : { name: provider, Icon: Plug, colors: 'text-gray-500 bg-gray-500/10' };

  const allFilled = help.fields.filter(f => !(help.optional || []).includes(f)).every(f => fields[f]?.trim());

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="setup-wizard-dialog">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className={`h-9 w-9 rounded-lg flex items-center justify-center ${providerInfo.colors}`}>
              <providerInfo.Icon className="h-5 w-5" />
            </div>
            <div>
              <DialogTitle>Connect {providerInfo.name}</DialogTitle>
              <DialogDescription>
                {step === 0 ? 'Follow these steps to get your API credentials' : 'Enter your credentials below'}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {step === 0 ? (
          <div className="space-y-4 py-2">
            <div className="space-y-3">
              {help.steps.map((s, i) => (
                <div key={i} className="flex gap-3 items-start">
                  <div className="h-6 w-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-bold shrink-0 mt-0.5">
                    {i + 1}
                  </div>
                  <p className="text-sm text-muted-foreground">{s}</p>
                </div>
              ))}
            </div>
            <div className="flex items-center gap-2 p-3 rounded-lg bg-blue-500/5 border border-blue-500/20">
              <Shield className="h-4 w-4 text-blue-500 shrink-0" />
              <p className="text-xs text-blue-600">
                Your credentials are encrypted at rest and never exposed in API responses.
              </p>
            </div>
          </div>
        ) : (
          <div className="space-y-4 py-2">
            {help.fields.map(field => {
              const isSensitive = !['phone_number', 'smtp_host', 'smtp_port', 'smtp_from_email', 'smtp_from_name', 'imap_host', 'imap_port', 'region', 'applet_id', 'elevenlabs_phone_id', 'sender_email'].includes(field);
              // Exotel API cluster → constrained dropdown (a free-text host like
              // "Singapore" breaks both the test call and ElevenLabs registration).
              if (provider === 'exotel' && field === 'region') {
                return (
                  <div key={field} className="space-y-1.5">
                    <label className="text-sm font-medium">{help.labels[field] || field}</label>
                    <select
                      className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
                      value={fields[field] || ''}
                      onChange={(e) => setFields(prev => ({ ...prev, [field]: e.target.value }))}
                      data-testid="wizard-input-region"
                    >
                      <option value="">Auto (recommended — picks India/Global from your number)</option>
                      <option value="api.in.exotel.com">India (api.in.exotel.com)</option>
                      <option value="api.exotel.com">Global (api.exotel.com)</option>
                    </select>
                    <p className="text-xs text-muted-foreground">Your Exotel API cluster — not a city. Leave on Auto unless Exotel told you otherwise.</p>
                  </div>
                );
              }
              return (
                <div key={field} className="space-y-1.5">
                  <label className="text-sm font-medium">{help.labels[field] || field}</label>
                  <Input
                    type={isSensitive ? 'password' : 'text'}
                    placeholder={help.placeholders[field] || ''}
                    value={fields[field] || ''}
                    onChange={(e) => setFields(prev => ({ ...prev, [field]: e.target.value }))}
                    data-testid={`wizard-input-${field}`}
                  />
                </div>
              );
            })}
            {provider === 'twilio' && (
              <div className="space-y-2 pt-2 border-t border-border/40" data-testid="caller-id-pool-editor">
                <div className="flex items-center gap-2">
                  <Globe className="h-4 w-4 text-muted-foreground" />
                  <label className="text-sm font-medium">Country caller-ID pool (optional)</label>
                </div>
                <p className="text-xs text-muted-foreground">
                  Route outbound calls through a local number per lead country. Leave empty to always use your main number.
                </p>
                {(fields._caller_pool || []).map((row, idx) => (
                  <div key={idx} className="flex gap-2 items-center">
                    <Input
                      className="w-24"
                      placeholder="US"
                      value={row.country || ''}
                      onChange={(e) => setFields(prev => {
                        const pool = [...(prev._caller_pool || [])];
                        pool[idx] = { ...pool[idx], country: e.target.value.toUpperCase() };
                        return { ...prev, _caller_pool: pool };
                      })}
                      data-testid={`caller-pool-country-${idx}`}
                    />
                    <Input
                      className="flex-1"
                      placeholder="+1 415 555 0100"
                      value={row.number || ''}
                      onChange={(e) => setFields(prev => {
                        const pool = [...(prev._caller_pool || [])];
                        pool[idx] = { ...pool[idx], number: e.target.value };
                        return { ...prev, _caller_pool: pool };
                      })}
                      data-testid={`caller-pool-number-${idx}`}
                    />
                    <Button
                      type="button" variant="ghost" size="sm"
                      className="text-red-500 hover:text-red-600"
                      onClick={() => setFields(prev => ({
                        ...prev,
                        _caller_pool: (prev._caller_pool || []).filter((_, i) => i !== idx),
                      }))}
                      data-testid={`caller-pool-remove-${idx}`}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                ))}
                <Button
                  type="button" variant="outline" size="sm"
                  onClick={() => setFields(prev => ({
                    ...prev,
                    _caller_pool: [...(prev._caller_pool || []), { country: '', number: '' }],
                  }))}
                  data-testid="caller-pool-add-btn"
                >
                  + Add country
                </Button>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          {step === 0 ? (
            <Button onClick={() => setStep(1)} data-testid="wizard-next-btn">
              I have my credentials <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          ) : (
            <div className="flex gap-2 w-full">
              <Button variant="outline" onClick={() => setStep(0)} data-testid="wizard-back-btn">
                Back
              </Button>
              <Button
                className="flex-1"
                disabled={!allFilled || saving}
                onClick={() => onSave(provider, fields)}
                data-testid="wizard-save-btn"
              >
                {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle className="h-4 w-4 mr-2" />}
                Save & Connect
              </Button>
            </div>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Google Calendar (Meeting Scheduler) card ───
function GoogleCalendarCard() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/meetings/google/status');
      setStatus(res.data || { connected: false });
    } catch {
      setStatus({ connected: false });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleConnect = async () => {
    setConnecting(true);
    try {
      const res = await api.get('/api/meetings/google/connect');
      const url = res.data?.authorization_url;
      if (url) {
        window.location.href = url;
      } else {
        toast.error('Could not start Google connect');
        setConnecting(false);
      }
    } catch (e) {
      toast.error('Could not start Google connect', { description: e.response?.data?.detail });
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Disconnect Google Calendar? Booking links will stop working.')) return;
    try {
      await api.post('/api/meetings/google/disconnect');
      toast.success('Google Calendar disconnected');
      load();
    } catch {
      toast.error('Disconnect failed');
    }
  };

  const connected = !!status?.connected;

  return (
    <Card className="relative overflow-hidden" data-testid="integration-card-google_calendar">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg flex items-center justify-center text-rose-500 bg-rose-500/10">
              <Calendar className="h-5 w-5" />
            </div>
            <div>
              <CardTitle className="text-base">Google Calendar</CardTitle>
              <CardDescription className="text-xs">Meeting scheduling & booking links</CardDescription>
            </div>
          </div>
          {connected ? (
            <Badge className="bg-green-500/10 text-green-600 border-green-500/20"><CheckCircle className="h-3 w-3 mr-1" />Connected</Badge>
          ) : (
            <Badge variant="outline" className="text-muted-foreground"><XCircle className="h-3 w-3 mr-1" />Not Connected</Badge>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <p className="text-xs text-muted-foreground mb-3">
          Connect your Google Calendar so leads can book meetings on your real availability and events sync automatically.
        </p>
        {loading ? (
          <Button size="sm" variant="outline" disabled><Spinner className="h-4 w-4 mr-1 animate-spin" />Loading…</Button>
        ) : connected ? (
          <div className="flex items-center gap-2">
            {status?.email && <span className="text-xs text-muted-foreground truncate max-w-[160px]">{status.email}</span>}
            <Button size="sm" variant="outline" onClick={handleDisconnect} data-testid="google-calendar-disconnect"><Trash2 className="h-4 w-4 mr-1" />Disconnect</Button>
          </div>
        ) : (
          <Button size="sm" onClick={handleConnect} disabled={connecting} data-testid="google-calendar-connect">
            {connecting ? <Spinner className="h-4 w-4 mr-1 animate-spin" /> : <Link2 className="h-4 w-4 mr-1" />}
            {connecting ? 'Connecting…' : 'Connect'}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}


// ─── Website Form / Zapier lead-capture card ───
function WebsiteFormCard() {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rotating, setRotating] = useState(false);
  const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/inbound/forms/settings');
      setCfg(res.data || null);
    } catch {
      setCfg(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const submitUrl = cfg?.submit_path ? `${API_BASE}${cfg.submit_path}` : '';

  const copy = async (text, label) => {
    try { await navigator.clipboard.writeText(text); toast.success(`${label} copied`); }
    catch { toast.error('Copy failed'); }
  };

  const rotate = async () => {
    if (!window.confirm('Rotate the token? Your existing form URL will stop working until you update it everywhere.')) return;
    setRotating(true);
    try {
      await api.post('/api/inbound/forms/settings/rotate');
      toast.success('Token rotated');
      await load();
    } catch {
      toast.error('Rotate failed');
    } finally {
      setRotating(false);
    }
  };

  return (
    <Card className="relative overflow-hidden md:col-span-2 lg:col-span-3" data-testid="website-form-card">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg flex items-center justify-center text-emerald-500 bg-emerald-500/10">
              <Globe className="h-5 w-5" />
            </div>
            <div>
              <CardTitle className="text-base">Website Form / Zapier Lead Capture</CardTitle>
              <CardDescription className="text-xs">
                Post leads from any website form, Typeform, Webflow, WordPress or Zapier — they land on your Kanban instantly, AI-scored.
              </CardDescription>
            </div>
          </div>
          <Badge className="bg-green-500/10 text-green-600 border-green-500/20"><CheckCircle className="h-3 w-3 mr-1" />Always On</Badge>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <Button size="sm" variant="outline" disabled><Spinner className="h-4 w-4 mr-1 animate-spin" />Loading…</Button>
        ) : (
          <div className="space-y-3">
            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground">Your form POST endpoint (send JSON or form-encoded)</label>
              <div className="flex gap-2">
                <Input readOnly value={submitUrl} className="font-mono text-xs" data-testid="website-form-url" />
                <Button size="sm" variant="outline" onClick={() => copy(submitUrl, 'URL')} data-testid="copy-form-url-btn"><Copy className="h-4 w-4" /></Button>
              </div>
            </div>
            <div className="text-xs text-muted-foreground rounded-lg bg-muted/30 border p-3 space-y-1">
              <p className="font-medium text-foreground">How to use</p>
              <p>• Map your form fields to <code>name</code>, <code>email</code>, <code>phone</code>, <code>company</code>, <code>message</code> (common synonyms accepted).</p>
              <p>• Add a hidden field named <code>_hp</code> (leave it empty) to silently block bot spam.</p>
              <p>• At least an <code>email</code> or <code>phone</code> is required. New leads are AI-scored and auto-enrolled in cadence.</p>
            </div>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" onClick={rotate} disabled={rotating} data-testid="rotate-form-token-btn">
                {rotating ? <Spinner className="h-4 w-4 mr-1 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1" />}Rotate token
              </Button>
              <span className="text-[11px] text-muted-foreground">Rotating invalidates the current URL.</span>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Website AI-Chat widget card ───
function ChatWidgetCard() {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ enabled: true, company_name: '', greeting: '', primary_color: '#7c3aed', ai_message_limit: 6 });
  const APP_BASE = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

  const widgetUrl = cfg?.widget_path ? `${APP_BASE}${cfg.widget_path}` : '';
  const embedSnippet = cfg?.token
    ? `<script>(function(){var s=document.createElement("script");s.src="${APP_BASE}/lockene-chat.js";s.async=true;s.setAttribute("data-token","${cfg.token}");s.setAttribute("data-app","${APP_BASE}");document.head.appendChild(s);})();</script>`
    : '';

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/inbound/chat/settings');
      setCfg(res.data);
      setForm({
        enabled: res.data.enabled, company_name: res.data.company_name || '',
        greeting: res.data.greeting || '', primary_color: res.data.primary_color || '#7c3aed',
        ai_message_limit: res.data.ai_message_limit || 6,
      });
    } catch { setCfg(null); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (patch) => {
    setSaving(true);
    try {
      const next = { ...form, ...patch };
      setForm(next);
      await api.put('/api/inbound/chat/settings', next);
      toast.success('Chat widget updated');
      await load();
    } catch { toast.error('Save failed'); } finally { setSaving(false); }
  };

  const copy = async (text, label) => {
    try { await navigator.clipboard.writeText(text); toast.success(`${label} copied`); }
    catch { toast.error('Copy failed'); }
  };

  return (
    <Card className="relative overflow-hidden md:col-span-2 lg:col-span-3" data-testid="chat-widget-card">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg flex items-center justify-center text-violet-500 bg-violet-500/10">
              <MessageCircle className="h-5 w-5" />
            </div>
            <div>
              <CardTitle className="text-base">Website AI-Chat Widget</CardTitle>
              <CardDescription className="text-xs">
                Drop a chat bubble on your site. The AI answers visitors, captures leads, and hands off to a human when needed.
              </CardDescription>
            </div>
          </div>
          {!loading && (
            <div className="flex items-center gap-2">
              <Switch checked={form.enabled} onCheckedChange={(v) => save({ enabled: v })} data-testid="chat-widget-enabled-toggle" />
              <span className="text-xs text-muted-foreground">{form.enabled ? 'On' : 'Off'}</span>
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <Button size="sm" variant="outline" disabled><Spinner className="h-4 w-4 mr-1 animate-spin" />Loading…</Button>
        ) : (
          <div className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Display name</Label>
                <Input value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })}
                  onBlur={() => save({})} className="text-xs" data-testid="chat-widget-name-input" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Brand color</Label>
                <div className="flex gap-2">
                  <input type="color" value={form.primary_color} onChange={(e) => save({ primary_color: e.target.value })}
                    className="h-9 w-12 rounded border bg-transparent cursor-pointer" data-testid="chat-widget-color-input" />
                  <Input value={form.primary_color} onChange={(e) => setForm({ ...form, primary_color: e.target.value })}
                    onBlur={() => save({})} className="text-xs font-mono" />
                </div>
              </div>
              <div className="space-y-1 sm:col-span-2">
                <Label className="text-xs text-muted-foreground">Greeting message</Label>
                <Input value={form.greeting} onChange={(e) => setForm({ ...form, greeting: e.target.value })}
                  onBlur={() => save({})} className="text-xs" data-testid="chat-widget-greeting-input" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">AI replies before human handoff</Label>
                <Input type="number" min={1} max={20} value={form.ai_message_limit}
                  onChange={(e) => setForm({ ...form, ai_message_limit: parseInt(e.target.value || '6', 10) })}
                  onBlur={() => save({})} className="text-xs w-28" data-testid="chat-widget-limit-input" />
              </div>
            </div>

            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Embed snippet — paste before <code>{'</body>'}</code> on your website</Label>
              <div className="flex gap-2">
                <Textarea readOnly value={embedSnippet} rows={3} className="font-mono text-[11px]" data-testid="chat-widget-embed" />
                <Button size="sm" variant="outline" onClick={() => copy(embedSnippet, 'Snippet')} data-testid="copy-chat-embed-btn"><Copy className="h-4 w-4" /></Button>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Button size="sm" variant="outline" onClick={() => window.open(widgetUrl, '_blank')} data-testid="preview-chat-widget-btn">
                <ExternalLink className="h-4 w-4 mr-1" />Preview widget
              </Button>
              <Button size="sm" variant="outline" onClick={() => copy(widgetUrl, 'Widget URL')}><Copy className="h-4 w-4 mr-1" />Copy URL</Button>
              {saving && <Spinner className="h-4 w-4 animate-spin text-muted-foreground" />}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}


// ─── Social & Messaging channels link card → Social Connectors page ───
function SocialChannelsCard() {
  const navigate = useNavigate();
  return (
    <Card
      className="relative overflow-hidden cursor-pointer transition-shadow hover:shadow-md"
      data-testid="social-channels-card"
      onClick={() => navigate('/social-connectors')}
    >
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg flex items-center justify-center text-sky-500 bg-sky-500/10">
              <MessageCircle className="h-5 w-5" />
            </div>
            <div>
              <CardTitle className="text-base">Social & Messaging Channels</CardTitle>
              <CardDescription className="text-xs">WhatsApp, Meta (Facebook/Instagram) DMs & Lead Ads, Telegram</CardDescription>
            </div>
          </div>
          <ChevronRight className="h-5 w-5 text-muted-foreground" />
        </div>
      </CardHeader>
      <CardContent>
        <Button
          size="sm"
          variant="outline"
          data-testid="open-social-connectors-btn"
          onClick={(e) => { e.stopPropagation(); navigate('/social-connectors'); }}
        >
          <Plug className="h-4 w-4 mr-1" />Connect channels
        </Button>
      </CardContent>
    </Card>
  );
}

// ─── Main Integrations Page ───
function VoiceReadinessCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const runCheck = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/voice/readiness');
      setData(res.data);
    } catch (e) {
      toast.error('Voice readiness check failed');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { runCheck(); }, [runCheck]);

  const rowIcon = (c) => {
    if (c.ok) return <CheckCircle className="h-3.5 w-3.5 text-green-600 shrink-0" />;
    if (c.level === 'optional') return <AlertTriangle className="h-3.5 w-3.5 text-amber-500 shrink-0" />;
    return <XCircle className="h-3.5 w-3.5 text-red-500 shrink-0" />;
  };

  return (
    <Card
      className="border border-border rounded-lg overflow-hidden md:col-span-2 lg:col-span-3 bg-gradient-to-r from-violet-500/5 to-transparent"
      data-testid="voice-readiness-card"
    >
      <CardContent className="pt-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-violet-500/10 text-violet-600 flex items-center justify-center shrink-0">
              <AudioLines className="h-5 w-5" />
            </div>
            <div>
              <h3 className="font-semibold text-base">Voice Readiness</h3>
              <p className="text-xs text-muted-foreground">
                Pre-flight check before placing AI calls — validates keys, webhooks &amp; telephony
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {data && (
              data.ready ? (
                <Badge variant="outline" className="bg-green-500/5 border-green-500/30 text-green-600" data-testid="voice-readiness-status">
                  <CheckCircle className="h-3 w-3 mr-1" />Ready to call
                </Badge>
              ) : (
                <Badge variant="outline" className="bg-red-500/5 border-red-500/30 text-red-600" data-testid="voice-readiness-status">
                  <XCircle className="h-3 w-3 mr-1" />{data.blocking_count} blocking
                </Badge>
              )
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={runCheck}
              disabled={loading}
              data-testid="voice-readiness-recheck-btn"
            >
              {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
              <span className="ml-1">Re-check</span>
            </Button>
          </div>
        </div>

        {data?.checks && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {data.checks.map((c) => (
              <div
                key={c.key}
                className="flex items-start gap-2 p-2 rounded bg-muted/30 border border-border/50"
                data-testid={`voice-readiness-check-${c.key}`}
              >
                {rowIcon(c)}
                <div className="min-w-0">
                  <p className="text-xs font-medium capitalize">{c.key.replace(/_/g, ' ')}</p>
                  <p className="text-[11px] text-muted-foreground leading-snug">{c.detail}</p>
                </div>
              </div>
            ))}
          </div>
        )}
        {data && (
          <p className="text-[11px] text-muted-foreground mt-3">
            Agent template <span className="font-mono">{data.agent_template_version}</span> · optional telephony rows
            only matter for the regions you call.
          </p>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Twilio Dedicated Number (platform-managed search & buy) ───
function TwilioNumberCard() {
  const [owned, setOwned] = useState([]);
  const [country, setCountry] = useState('US');
  const [areaCode, setAreaCode] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [buying, setBuying] = useState('');
  // Test call (Twilio-forced) — proves THIS caller ID works end-to-end, grounded in your KB
  const [testNumber, setTestNumber] = useState('');
  const [testAccent, setTestAccent] = useState('us_english');
  const [testing, setTesting] = useState(false);
  const [routePreview, setRoutePreview] = useState(null);

  // Routing preview — dry-run carrier + accent for the typed number (no credit spent)
  useEffect(() => {
    const num = (testNumber || '').trim();
    if (num.length < 6) { setRoutePreview(null); return; }
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const { data } = await voiceAPI.routingPreview(num);
        if (!cancelled) {
          setRoutePreview(data);
          if (['us_english', 'indian_english', 'hinglish'].includes(data.accent)) setTestAccent(data.accent);
        }
      } catch { if (!cancelled) setRoutePreview(null); }
    }, 450);
    return () => { cancelled = true; clearTimeout(t); };
  }, [testNumber]);

  const placeTest = async () => {
    const num = (testNumber || '').trim();
    if (!/^\+\d{8,15}$/.test(num)) {
      toast.error('Enter a number in international format, e.g. +14155550123');
      return;
    }
    setTesting(true);
    try {
      // force provider=twilio so this box always proves the Twilio caller ID itself
      const res = await voiceAPI.placeVoiceTestCall({ to_number: num, accent: testAccent, provider: 'twilio' });
      const d = res.data || {};
      if (d.status === 'simulated') toast.info(d.message || 'Simulated mode — no real call placed.');
      else toast.success(`Calling ${d.to_number} via Twilio — the AI is dialing now.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Test call failed');
    } finally { setTesting(false); }
  };

  const load = useCallback(async () => {
    try {
      const res = await voiceAPI.listNumbers();
      setOwned(res.data.numbers || []);
    } catch { /* noop */ } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const activeRec = owned.find((n) => n.status === 'active');

  const handleSearch = async () => {
    setSearching(true); setResults([]);
    try {
      const res = await voiceAPI.searchNumbers(country.toUpperCase(), areaCode.trim());
      setResults(res.data.numbers || []);
      if (!(res.data.numbers || []).length) toast.info('No numbers found. Try a different area code.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Search failed');
    } finally { setSearching(false); }
  };

  const handleBuy = async (phone) => {
    if (!window.confirm(`Buy ${phone}? A monthly rental will be charged to your credits.`)) return;
    setBuying(phone);
    try {
      await voiceAPI.purchaseNumber(phone);
      toast.success(`${phone} is now your dedicated caller ID`);
      setResults([]); await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Purchase failed');
    } finally { setBuying(''); }
  };

  const handleRelease = async (sid, phone) => {
    if (!window.confirm(`Release ${phone}? This stops the monthly rental and removes the number.`)) return;
    try {
      await voiceAPI.releaseNumber(sid);
      toast.success(`${phone} released`); await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Release failed');
    }
  };

  return (
    <Card className="md:col-span-2 lg:col-span-3" data-testid="twilio-number-card">
      <CardHeader className="pb-3">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-lg flex items-center justify-center text-red-500 bg-red-500/10">
            <Phone className="h-5 w-5" />
          </div>
          <div>
            <CardTitle className="text-base">Twilio — Get a Number</CardTitle>
            <CardDescription className="text-xs">
              Buy a dedicated, platform-managed number as your outbound caller ID (global). No API keys needed — monthly rental is billed to your credits.
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading ? (
          <div className="h-12 w-full bg-muted/40 rounded animate-pulse" />
        ) : activeRec ? (
          <div className="space-y-4">
            <div className="flex items-center justify-between rounded-lg border p-4 bg-muted/30" data-testid="twilio-active-number">
              <div className="flex items-center gap-3">
                <CheckCircle className="h-5 w-5 text-green-600" />
                <div>
                  <p className="font-semibold">{activeRec.phone_number}</p>
                  <p className="text-xs text-muted-foreground">Active caller ID · managed</p>
                </div>
              </div>
              <Button variant="outline" size="sm" className="text-red-600" data-testid="twilio-number-release-btn"
                onClick={() => handleRelease(activeRec.twilio_sid, activeRec.phone_number)}>
                <Trash2 className="h-4 w-4 mr-1" /> Release
              </Button>
            </div>

            {/* Test call — forced via Twilio, grounded in your Knowledge Base */}
            <div className="pt-1 space-y-2">
              <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Place a test call (via Twilio)</div>
              <div className="flex flex-wrap items-center gap-2">
                <Input
                  value={testNumber}
                  onChange={(e) => setTestNumber(e.target.value)}
                  placeholder="+14155550123"
                  className="h-9 w-48"
                  data-testid="twilio-test-number-input"
                />
                <select
                  value={testAccent}
                  onChange={(e) => setTestAccent(e.target.value)}
                  className="h-9 rounded-md border border-input bg-background px-2 text-sm"
                  data-testid="twilio-test-accent-select"
                >
                  <option value="us_english">English (US)</option>
                  <option value="indian_english">English (Indian)</option>
                  <option value="hinglish">Hinglish</option>
                </select>
                <Button size="sm" variant="outline" onClick={placeTest} disabled={testing} data-testid="twilio-test-call-btn">
                  {testing ? <Spinner className="h-4 w-4 mr-1.5 animate-spin" /> : <PhoneOutgoing className="h-4 w-4 mr-1.5" />}
                  Test call
                </Button>
              </div>
              <p className="text-[11px] text-muted-foreground">
                Always dials from your Twilio caller ID ({activeRec.phone_number}). The AI speaks grounded in your Knowledge Base;
                the accent picks the ElevenLabs voice (US → "Alex", Indian/Hinglish → "Arjun"). Uses 1 voice-call credit.
              </p>
              {routePreview && (
                <div className="flex flex-wrap items-center gap-1.5 text-[11px]" data-testid="twilio-routing-preview">
                  <span className="text-muted-foreground">Carrier</span>
                  <Badge className="text-[10px] px-1.5 py-0 bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300" data-testid="twilio-routing-preview-provider">
                    Twilio (forced)
                  </Badge>
                  <span className="text-muted-foreground">·</span>
                  <Badge variant="outline" className="text-[10px] px-1.5 py-0" data-testid="twilio-routing-preview-accent">
                    {routePreview.accent_label} · {routePreview.market_label}
                  </Badge>
                </div>
              )}
            </div>
          </div>
        ) : (
          <>
            <div className="flex flex-wrap items-end gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Country</Label>
                <Input className="w-20 uppercase" maxLength={2} value={country}
                  data-testid="twilio-number-country-input" onChange={(e) => setCountry(e.target.value)} />
              </div>
              <div className="space-y-1">
                <Label className="text-xs flex items-center gap-1"><MapPin className="h-3 w-3" /> Area code (optional)</Label>
                <Input className="w-32" placeholder="e.g. 415" value={areaCode}
                  data-testid="twilio-number-areacode-input" onChange={(e) => setAreaCode(e.target.value)} />
              </div>
              <Button onClick={handleSearch} disabled={searching} data-testid="twilio-number-search-btn">
                {searching ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Search className="h-4 w-4 mr-1" />}
                Search
              </Button>
            </div>
            {results.length > 0 && (
              <div className="space-y-2" data-testid="twilio-number-results">
                {results.map((n) => (
                  <div key={n.phone_number} className="flex items-center justify-between rounded-lg border p-3"
                    data-testid={`twilio-number-result-${n.phone_number}`}>
                    <div>
                      <p className="font-medium">{n.friendly_name || n.phone_number}</p>
                      <p className="text-xs text-muted-foreground">{[n.locality, n.region].filter(Boolean).join(', ') || n.iso_country}</p>
                    </div>
                    <Button size="sm" disabled={buying === n.phone_number}
                      data-testid={`twilio-number-buy-btn-${n.phone_number}`} onClick={() => handleBuy(n.phone_number)}>
                      {buying === n.phone_number ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
                      Buy
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

// ─── VoiceLink (India managed number — KYC-gated buy) ───
const VL_KYC_STEPS = [
  { key: 'step-1-register-details', title: 'Business details',
    required: ['account_type', 'full_name', 'phone', 'billing_address', 'state', 'city', 'pincode'],
    tnc: true,
    fields: [
      { name: 'account_type', label: 'Account type', type: 'select', options: ['business', 'individual'] },
      { name: 'full_name', label: 'Full name' },
      { name: 'business_name', label: 'Business name (business only)' },
      { name: 'email', label: 'Email' },
      { name: 'phone', label: 'Phone' },
      { name: 'billing_address', label: 'Billing address' },
      { name: 'state', label: 'State' },
      { name: 'city', label: 'City' },
      { name: 'pincode', label: 'Pincode' },
    ] },
  { key: 'step-2-pan-verify', title: 'PAN verification', doc: 'pan',
    required: ['pan_holder_name', 'pan_number'],
    fields: [
      { name: 'pan_holder_name', label: 'PAN holder name' },
      { name: 'pan_number', label: 'PAN number (ABCDE1234F)' },
    ] },
  { key: 'step-3-aadhaar-init', title: 'Aadhaar (DigiLocker)', aadhaar: true, fields: [] },
  { key: 'step-4-gst-verify', title: 'GST (business only)', doc: 'gst',
    required: ['gst_number'],
    fields: [{ name: 'gst_number', label: 'GST number (15 chars)' }] },
  { key: 'final-submit', title: 'Submit for verification', fields: [], final: true },
];

function VoiceLinkCard() {
  const [health, setHealth] = useState(null);
  const [kyc, setKyc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  // KYC wizard
  const [kycOpen, setKycOpen] = useState(false);
  const [stepIdx, setStepIdx] = useState(0);
  const [form, setForm] = useState({ account_type: 'business' });
  const [tncAccepted, setTncAccepted] = useState(false);
  // Buy dialog
  const [buyOpen, setBuyOpen] = useState(false);
  const [numbers, setNumbers] = useState([]);
  const [bundleCredits, setBundleCredits] = useState(1800);
  const [buying, setBuying] = useState('');
  const [didType, setDidType] = useState(2);
  const [loadingNums, setLoadingNums] = useState(false);
  // Adopt an existing VoiceLink number (admin: number already owned on the panel)
  const [adoptOpen, setAdoptOpen] = useState(false);
  const [adoptPhone, setAdoptPhone] = useState('');
  const [adoptDid, setAdoptDid] = useState('');
  const [adoptClientId, setAdoptClientId] = useState('');
  // Test call
  const [testNumber, setTestNumber] = useState('');
  const [testAccent, setTestAccent] = useState('hinglish');
  const [testing, setTesting] = useState(false);
  const [routePreview, setRoutePreview] = useState(null);
  // Aadhaar (step 3) — DigiLocker opened in another tab; we must re-check VoiceLink status before advancing
  const [aadhaarInitiated, setAadhaarInitiated] = useState(false);
  const [docFile, setDocFile] = useState(null);
  // Live VoiceLink :443 connectivity probe (green the moment VoiceLink allowlists us)
  const [conn, setConn] = useState(null);
  const [connChecking, setConnChecking] = useState(false);

  const checkConnectivity = useCallback(async (silent = true) => {
    setConnChecking(true);
    try {
      const res = await voiceAPI.voicelinkConnectivity();
      setConn(res.data || null);
      if (!silent) {
        if (res.data?.reachable) toast.success(`VoiceLink is reachable (${res.data.latency_ms}ms) — you're unblocked!`);
        else toast.error(res.data?.error || 'VoiceLink is still unreachable.');
      }
    } catch {
      setConn({ reachable: false, error: 'Could not run the connectivity check.' });
    } finally { setConnChecking(false); }
  }, []);
  useEffect(() => { checkConnectivity(true); }, [checkConnectivity]);

  const load = useCallback(async () => {
    try {
      const [h, k] = await Promise.all([
        voiceAPI.getInboundHealth().catch(() => ({ data: {} })),
        voiceAPI.getVoicelinkKyc().catch(() => ({ data: null })),
      ]);
      const vl = (h.data?.providers || []).find((p) => p.provider === 'voicelink');
      setHealth(vl || null);
      setKyc(k.data || null);
    } catch { /* noop */ } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Routing preview — dry-run the carrier + accent for the typed number (no credit spent)
  useEffect(() => {
    const num = (testNumber || '').trim();
    if (num.length < 6) { setRoutePreview(null); return; }
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const { data } = await voiceAPI.routingPreview(num);
        if (!cancelled) {
          setRoutePreview(data);
          // Auto-match the accent to the detected market (US number → English (US),
          // Indian → Hinglish) so the test agent talks the way it was designed.
          // Only sync the 3 supported dropdown values — leave the user's choice for
          // any other market (avoids an unsupported-accent error).
          if (['us_english', 'indian_english', 'hinglish'].includes(data.accent)) {
            setTestAccent(data.accent);
          }
        }
      } catch { if (!cancelled) setRoutePreview(null); }
    }, 450);
    return () => { cancelled = true; clearTimeout(t); };
  }, [testNumber]);


  const verified = !!kyc?.verified;
  const hasNumber = !!health?.phone_number && health?.verdict !== 'not_configured';

  const advanceStep = () => {
    if (stepIdx < VL_KYC_STEPS.length - 1) {
      let next = stepIdx + 1;
      // skip GST for individuals
      if (VL_KYC_STEPS[next]?.key === 'step-4-gst-verify' && form.account_type === 'individual') next += 1;
      setStepIdx(next);
    } else {
      setKycOpen(false); setStepIdx(0);
      load();
    }
  };

  // Re-check Aadhaar status with VoiceLink after the user finishes DigiLocker.
  // Only advances to the next step once VoiceLink reports aadhaar_verified — this
  // prevents the downstream "Please verify your Aadhaar first (Step 3)" GST error.
  const recheckAadhaar = async (silent = false) => {
    setBusy(true);
    try {
      const k = await voiceAPI.getVoicelinkKyc();
      setKyc(k.data || null);
      if (k.data?.kyc?.aadhaar_verified) {
        // VoiceLink also requires the Aadhaar DOCUMENT uploaded (like PAN) before KYC can proceed.
        if (!docFile) {
          if (!silent) toast.error('Aadhaar verified! Now attach your Aadhaar document below, then re-check to continue.');
          setBusy(false); return;
        }
        try {
          await voiceAPI.uploadVoicelinkKycDocument('aadhaar', docFile);
        } catch (e) {
          toast.error(e?.response?.data?.detail || 'Aadhaar document upload failed. Please try again.');
          setBusy(false); return;
        }
        setDocFile(null);
        setAadhaarInitiated(false);
        toast.success('Aadhaar verified & document uploaded!');
        advanceStep();
      } else if (!silent) {
        toast.error('Aadhaar not verified yet. Finish DigiLocker in the other tab (log in if you already have an account, enter your Aadhaar and click Verify), then re-check.');
      }
    } catch {
      if (!silent) toast.error('Could not check Aadhaar status. Please try again.');
    } finally { setBusy(false); }
  };

  const submitStep = async () => {
    const step = VL_KYC_STEPS[stepIdx];
    const missing = (step.required || []).filter((n) => !String(form[n] || '').trim());
    if (missing.length) {
      const labels = missing.map((n) => (step.fields || []).find((f) => f.name === n)?.label || n);
      toast.error(`Please fill in: ${labels.join(', ')}`);
      return;
    }
    if (step.tnc && !tncAccepted) {
      toast.error("Please accept VoiceLink's Terms & Conditions to continue.");
      return;
    }
    if (step.doc && !docFile) {
      toast.error(`Please attach your ${step.doc.toUpperCase()} card/document to continue.`);
      return;
    }
    setBusy(true);
    try {
      // Aadhaar step: initiate DigiLocker in a new tab, but DO NOT auto-advance.
      // Wait until VoiceLink records aadhaar_verified (user re-checks / tab refocus).
      if (step.aadhaar) {
        const res = await voiceAPI.submitVoicelinkKycStep(step.key, {});
        const url = res.data?.data?.redirect_url || res.data?.data?.url;
        setAadhaarInitiated(true); // always enable Re-check, even if no URL came back
        if (url) {
          window.open(url, '_blank', 'noopener');
          toast.info('DigiLocker opened in a new tab. Complete Aadhaar there, then click "I\'ve completed Aadhaar — Re-check".');
        } else {
          toast.info('Aadhaar verification started. If DigiLocker did not open, click "Re-open DigiLocker", finish there, then "Re-check".');
        }
        return;
      }
      const payload = {};
      (step.fields || []).forEach((f) => { if (form[f.name]) payload[f.name] = form[f.name]; });
      if (step.tnc && tncAccepted) payload.term_and_condition = 1;
      const res = await voiceAPI.submitVoicelinkKycStep(step.key, payload);
      // VoiceLink requires the actual document (e.g. PAN card) uploaded before it
      // will advance KYC — even once the number is verified.
      if (step.doc) {
        await voiceAPI.uploadVoicelinkKycDocument(step.doc, docFile);
      }
      toast.success(res.data?.message || `${step.title} saved`);
      setDocFile(null);
      advanceStep();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'KYC step failed');
    } finally { setBusy(false); }
  };

  const loadNumbers = async (type) => {
    setLoadingNums(true); setNumbers([]);
    try {
      const res = await voiceAPI.getVoicelinkAvailable(type);
      setNumbers(res.data?.numbers || []);
      setBundleCredits(res.data?.bundle_credits_per_month || 1800);
      if (!(res.data?.numbers || []).length) toast.info('No numbers of this type available right now — try another type.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load available numbers');
    } finally { setLoadingNums(false); }
  };

  const openBuy = async () => {
    setBusy(true);
    setDidType(2);
    try {
      const res = await voiceAPI.getVoicelinkAvailable(2);
      setNumbers(res.data?.numbers || []);
      setBundleCredits(res.data?.bundle_credits_per_month || 1800);
      setBuyOpen(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load available numbers');
    } finally { setBusy(false); }
  };

  const selectType = (type) => { setDidType(type); loadNumbers(type); };

  const adoptNumber = async () => {
    const did = parseInt(adoptDid, 10);
    if (!adoptPhone.trim() || !did) { toast.error('Enter the phone number and its VoiceLink DID id.'); return; }
    setBusy(true);
    try {
      const payload = { did_id: did, phone_number: adoptPhone.trim() };
      if (adoptClientId.trim()) payload.client_id = parseInt(adoptClientId, 10);
      const res = await voiceAPI.adoptVoicelinkNumber(payload);
      toast.success(`${res.data?.phone_number} adopted — you can now reclaim it.`);
      setAdoptOpen(false); setAdoptPhone(''); setAdoptDid(''); setAdoptClientId(''); await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not adopt this number');
    } finally { setBusy(false); }
  };

  const buy = async (didId, num) => {
    if (!window.confirm(`Buy ${num}? ${bundleCredits} credits/month (1 number + 3 channels) will be charged now and every month.`)) return;
    setBuying(didId);
    try {
      const res = await voiceAPI.buyVoicelinkNumber(didId);
      toast.success(`${res.data?.phone_number} is now your India AI number.`);
      setBuyOpen(false); await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Purchase failed');
    } finally { setBuying(''); }
  };

  const enableOutbound = async () => {
    setBusy(true);
    try {
      await voiceAPI.enableVoicelinkOutboundManaged();
      toast.success('Outbound enabled — the AI can now place calls over VoiceLink.');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not enable outbound');
    } finally { setBusy(false); }
  };

  const reconnectOutbound = async () => {
    setBusy(true);
    try {
      await voiceAPI.reconnectVoicelink();
      toast.success('Reconnected — outbound calling is re-enabled on this number.');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Reconnect failed');
    } finally { setBusy(false); }
  };

  const reclaimNumber = async () => {
    if (!window.confirm(`Reclaim your previous number ${health?.previous_phone_number || ''}? It re-attaches to your account and the AI resumes inbound & outbound. You won't be charged again if the current month is still paid.`)) return;
    setBusy(true);
    try {
      const res = await voiceAPI.reclaimVoicelinkNumber();
      toast.success(`${res.data?.phone_number || 'Your number'} is back${res.data?.within_paid_cycle ? ' — no extra charge this cycle.' : '.'}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not reclaim the number');
    } finally { setBusy(false); }
  };

  const releaseNumber = async () => {    if (!window.confirm('Release this VoiceLink number? The AI will stop taking/placing calls on it and monthly billing stops. This cannot be undone.')) return;
    setBusy(true);
    try {
      const res = await voiceAPI.releaseVoicelinkNumber();
      toast.success(`Released ${res.data?.phone_number || 'the number'}. You can buy a new one anytime.`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not release the number');
    } finally { setBusy(false); }
  };

  const placeTest = async () => {
    const num = (testNumber || '').trim();
    if (!/^\+\d{8,15}$/.test(num)) {
      toast.error('Enter a number in international format, e.g. +918668546474');
      return;
    }
    setTesting(true);
    try {
      const res = await voiceAPI.placeVoiceTestCall({ to_number: num, accent: testAccent });
      const d = res.data || {};
      if (d.status === 'simulated') toast.info(d.message || 'Simulated mode — no real call placed.');
      else toast.success(`Calling ${d.to_number} via ${d.provider} — the AI is dialing now.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Test call failed');
    } finally { setTesting(false); }
  };

  const step = VL_KYC_STEPS[stepIdx];

  // Resume point: jump to the first not-yet-completed step based on KYC flags.
  const kycFlags = kyc?.kyc || {};
  const registerDone = !!kycFlags.current_step && kycFlags.current_step !== 'register';
  const stepDone = (key) => (
    (key === 'step-1-register-details' && registerDone) ||
    (key === 'step-2-pan-verify' && kycFlags.pan_verified) ||
    (key === 'step-3-aadhaar-init' && kycFlags.aadhaar_verified) ||
    (key === 'step-4-gst-verify' && (kycFlags.gst_verified || form.account_type === 'individual'))
  );
  const resumeIdx = () => {
    const i = VL_KYC_STEPS.findIndex((s) => !s.final && !stepDone(s.key));
    return i < 0 ? 0 : i;
  };
  const openKyc = () => { setAadhaarInitiated(false); setTncAccepted(registerDone); setStepIdx(resumeIdx()); setKycOpen(true); };

  // When the user returns to this tab after finishing DigiLocker, silently re-check
  // Aadhaar status and auto-advance if VoiceLink has recorded it as verified.
  useEffect(() => {
    if (!kycOpen || !aadhaarInitiated) return;
    const onFocus = () => { recheckAadhaar(true); };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [kycOpen, aadhaarInitiated, stepIdx]);

  return (
    <Card className="md:col-span-2 lg:col-span-3" data-testid="voicelink-card">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg flex items-center justify-center text-cyan-500 bg-cyan-500/10">
              <Wifi className="h-5 w-5" />
            </div>
            <div>
              <CardTitle className="text-base">VoiceLink — India Number</CardTitle>
              <CardDescription className="text-xs">
                Managed by Lockene — no login needed. Complete KYC, then buy a dedicated India number (₹1,500/mo bundle = 1 number + 3 channels) for AI inbound &amp; outbound.
              </CardDescription>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => checkConnectivity(false)}
              disabled={connChecking}
              title={conn?.error || (conn?.reachable ? `Reachable (${conn?.latency_ms}ms)` : 'Click to re-check VoiceLink connection')}
              data-testid="voicelink-connectivity-chip"
              className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[11px] font-medium transition-colors ${
                connChecking ? 'border-border bg-muted/40 text-muted-foreground'
                : conn?.reachable ? 'border-green-500/30 bg-green-500/10 text-green-600'
                : 'border-red-500/30 bg-red-500/10 text-red-600'}`}
            >
              <span className={`h-1.5 w-1.5 rounded-full ${connChecking ? 'bg-muted-foreground animate-pulse' : conn?.reachable ? 'bg-green-500' : 'bg-red-500'}`} />
              {connChecking ? 'Checking…' : conn?.reachable ? 'VoiceLink online' : 'VoiceLink blocked'}
              {connChecking ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3 opacity-60" />}
            </button>
          {hasNumber ? (
            <Badge variant="outline" className="bg-green-500/5 border-green-500/30 text-green-600" data-testid="voicelink-status">
              <CheckCircle className="h-3 w-3 mr-1" />Active
            </Badge>
          ) : verified ? (
            <Badge variant="outline" className="bg-green-500/5 border-green-500/30 text-green-600" data-testid="voicelink-status">
              <BadgeCheck className="h-3 w-3 mr-1" />KYC verified
            </Badge>
          ) : (
            <Badge variant="outline" className="bg-amber-500/5 border-amber-500/30 text-amber-600" data-testid="voicelink-status">
              <AlertTriangle className="h-3 w-3 mr-1" />KYC required
            </Badge>
          )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading ? (
          <div className="h-12 w-full bg-muted/40 rounded animate-pulse" />
        ) : hasNumber ? (
          <>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 p-3 rounded-lg bg-muted/30 border border-border/50 text-sm">
              <div>
                <div className="text-[11px] uppercase tracking-wide text-muted-foreground">DID number</div>
                <div className="font-medium truncate">{health?.phone_number}</div>
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-wide text-muted-foreground">AI phone</div>
                <div className={`font-medium ${health?.has_elevenlabs_phone ? 'text-green-600' : 'text-muted-foreground'}`}>
                  {health?.has_elevenlabs_phone ? 'Registered' : '—'}
                </div>
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Outbound (AI dials)</div>
                <div className={`font-medium ${health?.outbound_enabled ? 'text-green-600' : 'text-muted-foreground'}`}>
                  {health?.outbound_enabled ? 'Enabled' : 'Off'}
                </div>
              </div>
            </div>
            <p className="text-[11px] text-muted-foreground">Billing: {bundleCredits} credits/month · charged monthly (1 number + 3 channels).</p>

            {/* Number health */}
            {(() => {
              const st = health?.voice_ai_status || 'ready';
              const map = {
                ready: { cls: 'bg-green-500/5 border-green-500/30 text-green-600', label: 'Healthy — active & billed' },
                billing_hold: { cls: 'bg-amber-500/5 border-amber-500/30 text-amber-600', label: 'Billing hold — last charge failed, will retry' },
                suspended_nonpayment: { cls: 'bg-red-500/5 border-red-500/30 text-red-600', label: 'Suspended — non-payment. Top up to recover' },
                release_pending: { cls: 'bg-red-500/5 border-red-500/30 text-red-600', label: 'Releasing — unpaid past grace period' },
                released: { cls: 'bg-red-500/5 border-red-500/30 text-red-600', label: 'Released — number returned. Buy a new one after top-up' },
              };
              const s = map[st] || map.ready;
              return (
                <div className={`flex items-center justify-between gap-3 p-2.5 rounded-lg border text-xs ${s.cls}`} data-testid="voicelink-health-status">
                  <span className="flex items-center gap-1.5"><Shield className="h-3.5 w-3.5" />{s.label}</span>
                  {st !== 'ready' && (
                    <a href="/billing" className="underline font-medium whitespace-nowrap" data-testid="voicelink-topup-link">Top up credits</a>
                  )}
                </div>
              );
            })()}

            {health?.verdict === 'ready' && !health?.outbound_enabled && (
              <Button size="sm" onClick={enableOutbound} disabled={busy} data-testid="voicelink-enable-outbound-btn">
                <PhoneOutgoing className="h-4 w-4 mr-1.5" />Enable outbound
              </Button>
            )}

            {/* Test call */}
            <div className="pt-3 border-t border-border/50 space-y-2">
              <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Place a test call</div>
              <div className="flex flex-wrap items-center gap-2">
                <Input
                  value={testNumber}
                  onChange={(e) => setTestNumber(e.target.value)}
                  placeholder="+918668546474"
                  className="h-9 w-48"
                  data-testid="voicelink-test-number-input"
                />
                <select
                  value={testAccent}
                  onChange={(e) => setTestAccent(e.target.value)}
                  className="h-9 rounded-md border border-input bg-background px-2 text-sm"
                  data-testid="voicelink-test-accent-select"
                >
                  <option value="hinglish">Hinglish</option>
                  <option value="indian_english">English (Indian)</option>
                  <option value="us_english">English (US)</option>
                </select>
                <Button size="sm" variant="outline" onClick={placeTest} disabled={testing} data-testid="voicelink-test-call-btn">
                  {testing ? <Spinner className="h-4 w-4 mr-1.5 animate-spin" /> : <PhoneOutgoing className="h-4 w-4 mr-1.5" />}
                  Test call
                </Button>
              </div>
              <p className="text-[11px] text-muted-foreground">Indian numbers auto-route via VoiceLink, others via Twilio. Uses 1 voice-call credit. TRAI window 10:00–21:00 IST for Indian numbers.</p>
              {routePreview && (
                <div className="flex flex-wrap items-center gap-1.5 text-[11px]" data-testid="routing-preview">
                  <span className="text-muted-foreground">Will route via</span>
                  <Badge className={`text-[10px] px-1.5 py-0 ${routePreview.provider === 'voicelink' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300' : routePreview.provider === 'exotel' ? 'bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300' : 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300'}`} data-testid="routing-preview-provider">
                    {routePreview.provider_label}
                  </Badge>
                  <span className="text-muted-foreground">·</span>
                  <Badge variant="outline" className="text-[10px] px-1.5 py-0" data-testid="routing-preview-accent">
                    {routePreview.accent_label} · {routePreview.market_label}
                  </Badge>
                  {routePreview.is_indian && !routePreview.trai_window_open && (
                    <Badge className="text-[10px] px-1.5 py-0 bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300" data-testid="routing-preview-trai">
                      TRAI window closed (10:00–21:00 IST)
                    </Badge>
                  )}
                </div>
              )}
            </div>

            <div className="pt-3 border-t border-border/50 flex items-center gap-2">
              <Button size="sm" variant="ghost" onClick={reconnectOutbound} disabled={busy}
                className="text-muted-foreground hover:text-foreground" data-testid="voicelink-reconnect-btn">
                <RefreshCw className="h-4 w-4 mr-1.5" />Reconnect (fix outbound)
              </Button>
              <Button size="sm" variant="ghost" onClick={releaseNumber} disabled={busy}
                className="text-red-600 hover:text-red-700 hover:bg-red-500/10" data-testid="voicelink-release-btn">
                <Trash2 className="h-4 w-4 mr-1.5" />Release number
              </Button>
            </div>
          </>
        ) : (
          <div className="space-y-2">
            {/* A released tenant that can_reclaim was ALREADY KYC-verified (you can't
                buy without it). Don't gate the banner on the live `verified` probe —
                that needs VoiceLink :443, so while the IP is blocked `verified` is
                false and the number would wrongly disappear from the UI. */}
            {health?.can_reclaim && (
              <div className="flex flex-wrap items-center justify-between gap-2 p-2.5 rounded-lg border border-cyan-500/30 bg-cyan-500/5" data-testid="voicelink-reclaim-banner">
                <span className="text-xs text-muted-foreground">
                  Your previous number <b className="text-foreground">{health.previous_phone_number}</b> can be reconnected — no new KYC, and no extra charge if this month is still paid.
                </span>
                <Button size="sm" onClick={reclaimNumber} disabled={busy} data-testid="voicelink-reclaim-btn">
                  {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1.5" />}Reclaim my number
                </Button>
              </div>
            )}
            <div className="flex flex-wrap items-center gap-2">
              {!verified && !health?.can_reclaim ? (
                <Button size="sm" onClick={openKyc} disabled={busy} data-testid="voicelink-kyc-btn">
                  <BadgeCheck className="h-4 w-4 mr-1.5" />Complete KYC
                </Button>
              ) : (
                <Button size="sm" variant={health?.can_reclaim ? 'outline' : 'default'} onClick={openBuy} disabled={busy} data-testid="voicelink-get-number-btn">
                  {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Search className="h-4 w-4 mr-1.5" />}{health?.can_reclaim ? 'Get a different number' : 'Get an India number'}
                </Button>
              )}
              <span className="text-[11px] text-muted-foreground">{bundleCredits} credits/mo · 1 number + 3 channels</span>
            </div>
            {!health?.can_reclaim && (
              <button type="button" onClick={() => setAdoptOpen(true)} className="text-[11px] text-cyan-500 hover:underline" data-testid="voicelink-adopt-link">
                Already own a VoiceLink number? Adopt it →
              </button>
            )}
          </div>
        )}
      </CardContent>

      {/* Adopt an existing VoiceLink number (admin) */}
      <Dialog open={adoptOpen} onOpenChange={setAdoptOpen}>
        <DialogContent className="max-w-md" data-testid="voicelink-adopt-dialog">
          <DialogHeader>
            <DialogTitle>Adopt an existing VoiceLink number</DialogTitle>
            <DialogDescription>Point this account at a DID you already own on the VoiceLink panel. This makes the “Reclaim my number” banner appear; reconnecting the number still needs VoiceLink reachable.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-1">
            <div>
              <label className="text-xs text-muted-foreground">Phone number (E.164)</label>
              <Input value={adoptPhone} onChange={(e) => setAdoptPhone(e.target.value)} placeholder="+919228130611" data-testid="voicelink-adopt-phone" />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">VoiceLink DID id</label>
              <Input value={adoptDid} onChange={(e) => setAdoptDid(e.target.value)} placeholder="e.g. 3003" data-testid="voicelink-adopt-did" />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">VoiceLink client id (optional — only if not already linked)</label>
              <Input value={adoptClientId} onChange={(e) => setAdoptClientId(e.target.value)} placeholder="e.g. 1919" data-testid="voicelink-adopt-client" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAdoptOpen(false)} data-testid="voicelink-adopt-cancel">Cancel</Button>
            <Button onClick={adoptNumber} disabled={busy} data-testid="voicelink-adopt-submit">
              {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : null}Adopt number
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* KYC wizard */}
      <Dialog open={kycOpen} onOpenChange={setKycOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] flex flex-col" data-testid="voicelink-kyc-dialog">
          <DialogHeader>
            <DialogTitle>VoiceLink KYC — {step?.title}</DialogTitle>
            <DialogDescription>Step {stepIdx + 1} of {VL_KYC_STEPS.length}. Required by TRAI before a number can go live.</DialogDescription>
          </DialogHeader>
          {/* Step tracker */}
          <div className="flex items-center gap-1.5 py-2" data-testid="voicelink-kyc-stepper">
            {VL_KYC_STEPS.map((s, i) => {
              const done = i < stepIdx || stepDone(s.key);
              const active = i === stepIdx;
              return (
                <div key={s.key} className="flex items-center gap-1.5 flex-1">
                  <div className={`h-6 w-6 shrink-0 rounded-full flex items-center justify-center text-[11px] font-semibold
                    ${done ? 'bg-green-500 text-white' : active ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}`}
                    data-testid={`voicelink-kyc-step-${i}`}>
                    {done ? <CheckCircle className="h-3.5 w-3.5" /> : i + 1}
                  </div>
                  {i < VL_KYC_STEPS.length - 1 && (
                    <div className={`h-0.5 flex-1 rounded ${done ? 'bg-green-500' : 'bg-muted'}`} />
                  )}
                </div>
              );
            })}
          </div>
          <div className="space-y-3 overflow-y-auto flex-1 min-h-0 -mx-1 px-1">
            {step?.aadhaar && (
              <div className="text-sm text-muted-foreground space-y-2" data-testid="voicelink-kyc-aadhaar-help">
                <p>Click <b className="text-foreground">Open DigiLocker</b> to verify your Aadhaar in a new tab.</p>
                <p><b className="text-foreground">Already have a DigiLocker account?</b> That&apos;s fine — log in with your existing DigiLocker / mobile credentials, enter your Aadhaar number and click <b>Verify</b>. The &quot;Aadhaar already registered&quot; message is normal and does not block you.</p>
                {aadhaarInitiated ? (
                  <p className="text-amber-600 dark:text-amber-500">After you finish in the DigiLocker tab, come back here, attach your Aadhaar document below, then click <b>I&apos;ve completed Aadhaar — Re-check</b>. We&apos;ll confirm with VoiceLink before moving on.</p>
                ) : (
                  <p>When done, return here and re-check so we can confirm with VoiceLink.</p>
                )}
                {aadhaarInitiated && (
                  <div className="space-y-1.5 pt-1" data-testid="voicelink-kyc-aadhaar-doc-upload">
                    <Label className="text-xs">Upload Aadhaar document <span className="text-red-500">*</span></Label>
                    <input
                      type="file"
                      accept="image/*,application/pdf"
                      onChange={(e) => setDocFile(e.target.files?.[0] || null)}
                      className="w-full text-sm file:mr-3 file:rounded-md file:border-0 file:bg-primary/10 file:px-3 file:py-1.5 file:text-sm file:text-primary hover:file:bg-primary/20 cursor-pointer"
                      data-testid="voicelink-kyc-aadhaar-doc-input"
                    />
                    {docFile ? (
                      <p className="text-[11px] text-emerald-500">Selected: {docFile.name}</p>
                    ) : (
                      <p className="text-[11px] text-muted-foreground">Required by VoiceLink/TRAI. JPG, PNG or PDF, up to 5 MB.</p>
                    )}
                  </div>
                )}
              </div>
            )}
            {step?.final && (
              <p className="text-sm text-muted-foreground">Submit your details for verification. You&apos;ll be able to buy a number once verified.</p>
            )}
            {(step?.fields || []).map((f) => (
              <div key={f.name} className="space-y-1.5">
                <Label className="text-xs">{f.label}</Label>
                {f.type === 'select' ? (
                  <select className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
                    value={form[f.name] || f.options[0]}
                    onChange={(e) => setForm((p) => ({ ...p, [f.name]: e.target.value }))}
                    data-testid={`voicelink-kyc-${f.name}`}>
                    {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                ) : (
                  <Input value={form[f.name] || ''} onChange={(e) => setForm((p) => ({ ...p, [f.name]: e.target.value }))}
                    data-testid={`voicelink-kyc-${f.name}`} />
                )}
              </div>
            ))}
            {step?.doc && (
              <div className="space-y-1.5 pt-1" data-testid="voicelink-kyc-doc-upload">
                <Label className="text-xs">Upload {step.doc.toUpperCase()} card/document <span className="text-red-500">*</span></Label>
                <input
                  type="file"
                  accept="image/*,application/pdf"
                  onChange={(e) => setDocFile(e.target.files?.[0] || null)}
                  className="w-full text-sm file:mr-3 file:rounded-md file:border-0 file:bg-primary/10 file:px-3 file:py-1.5 file:text-sm file:text-primary hover:file:bg-primary/20 cursor-pointer"
                  data-testid="voicelink-kyc-doc-input"
                />
                {docFile ? (
                  <p className="text-[11px] text-emerald-500">Selected: {docFile.name}</p>
                ) : (
                  <p className="text-[11px] text-muted-foreground">Required by VoiceLink/TRAI. JPG, PNG or PDF, up to 5 MB.</p>
                )}
              </div>
            )}
            {step?.tnc && (
              <label className="flex items-start gap-2 pt-1 text-xs text-muted-foreground cursor-pointer">
                <input
                  type="checkbox"
                  className="mt-0.5 h-4 w-4 shrink-0 rounded border-input accent-primary"
                  checked={tncAccepted}
                  onChange={(e) => setTncAccepted(e.target.checked)}
                  data-testid="voicelink-kyc-tnc-checkbox"
                />
                <span>
                  I accept the{' '}
                  <a href="https://lockene.ai/terms-and-conditions" target="_blank" rel="noopener noreferrer"
                     className="underline text-primary hover:opacity-80" data-testid="voicelink-kyc-tnc-link">
                    Terms &amp; Conditions
                  </a>{' '}
                  for VoiceLink telecom number allocation and confirm the
                  details above are accurate. These details are shared with VoiceLink, our telecom partner,
                  for mandatory KYC verification.
                </span>
              </label>
            )}
          </div>
          <DialogFooter className="pt-3 border-t border-border/50 shrink-0 flex-row flex-wrap items-center justify-end gap-2 sm:space-x-0">
            {stepIdx > 0 && (
              <Button size="sm" variant="ghost" className="mr-auto" onClick={() => { setStepIdx((i) => { let p = i - 1; if (VL_KYC_STEPS[p]?.key === 'step-4-gst-verify' && form.account_type === 'individual') p -= 1; return Math.max(0, p); }); setDocFile(null); setAadhaarInitiated(false); }} disabled={busy} data-testid="voicelink-kyc-back-btn">
                <ArrowLeft className="h-4 w-4 mr-1.5" />Back
              </Button>
            )}
            <Button size="sm" variant="outline" onClick={() => setKycOpen(false)} disabled={busy} data-testid="voicelink-kyc-cancel-btn">Cancel</Button>
            {step?.aadhaar && aadhaarInitiated ? (
              <>
                <Button size="sm" variant="outline" onClick={submitStep} disabled={busy} data-testid="voicelink-kyc-reopen-digilocker-btn">
                  <ArrowRight className="h-4 w-4 mr-1.5" />Re-open DigiLocker
                </Button>
                <Button size="sm" onClick={() => recheckAadhaar(false)} disabled={busy} data-testid="voicelink-kyc-recheck-aadhaar-btn">
                  {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1.5" />}
                  I&apos;ve completed Aadhaar — Re-check
                </Button>
              </>
            ) : (
              <Button size="sm" onClick={submitStep} disabled={busy || (step?.tnc && !tncAccepted)} data-testid="voicelink-kyc-next-btn">
                {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <ArrowRight className="h-4 w-4 mr-1.5" />}
                {step?.aadhaar ? 'Open DigiLocker' : step?.final ? 'Submit' : 'Continue'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Buy dialog */}
      <Dialog open={buyOpen} onOpenChange={setBuyOpen}>
        <DialogContent data-testid="voicelink-buy-dialog">
          <DialogHeader>
            <DialogTitle>Get an India number</DialogTitle>
            <DialogDescription>{bundleCredits} credits/month (1 number + 3 channels), charged now and every month — same price for every number type.</DialogDescription>
          </DialogHeader>
          <div className="flex flex-wrap gap-1.5 mb-1" data-testid="voicelink-type-selector">
            {[{ id: 2, label: 'Mobile' }, { id: 6, label: '92-Series' }, { id: 3, label: 'Landline' }, { id: 1, label: 'Toll-Free' }].map((t) => (
              <button key={t.id} onClick={() => selectType(t.id)} disabled={loadingNums}
                data-testid={`voicelink-type-${t.id}`}
                className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${didType === t.id ? 'bg-primary text-primary-foreground border-primary' : 'border-border text-muted-foreground hover:bg-muted'}`}>
                {t.label}
              </button>
            ))}
          </div>
          <div className="space-y-2 max-h-80 overflow-y-auto">
            {loadingNums ? (
              <div className="flex items-center justify-center py-8 text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin mr-2" />Loading numbers…</div>
            ) : numbers.length === 0 ? (
              <p className="text-sm text-muted-foreground py-6 text-center">No numbers of this type available right now.</p>
            ) : numbers.map((n) => (
              <div key={n.did_id} className="flex items-center justify-between rounded-lg border p-3" data-testid={`voicelink-number-${n.did_number}`}>
                <div>
                  <p className="font-medium">{n.did_number}</p>
                  <p className="text-xs text-muted-foreground">{n.type_label}</p>
                </div>
                <Button size="sm" disabled={buying === n.did_id} onClick={() => buy(n.did_id, n.did_number)} data-testid={`voicelink-buy-${n.did_number}`}>
                  {buying === n.did_id ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}Buy
                </Button>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
}


// ─── Channel Overview — the ONE place: which channels are connected + how much
// cadences & the AI Brain actually use each one ───
const CH_ICONS = { email: Mail, call: PhoneOutgoing, whatsapp: MessageCircle, linkedin: Link2 };
const CH_ACCENT = {
  email: 'text-sky-500 bg-sky-500/10',
  call: 'text-emerald-500 bg-emerald-500/10',
  whatsapp: 'text-green-500 bg-green-500/10',
  linkedin: 'text-indigo-500 bg-indigo-500/10',
};

function ChannelOverviewCard({ onOpenChannel }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const res = await cadencesAPI.getChannelOverview();
      setData(res.data || null);
    } catch { setData(null); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <Card className="mb-4" data-testid="channel-overview-card">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg flex items-center justify-center text-primary bg-primary/10 shrink-0">
              <Radio className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <CardTitle className="text-base">Channels — connected &amp; used by cadences + AI Brain</CardTitle>
              <CardDescription className="text-xs">
                Which outreach channels are wired, how much your cadences plan to use each, and touches sent in the last 30 days. Tap a card to connect or manage it.
              </CardDescription>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={load} disabled={loading} className="shrink-0" data-testid="channel-overview-refresh">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {[0, 1, 2, 3].map((i) => <div key={i} className="h-32 w-full bg-muted/40 rounded-xl animate-pulse" />)}
          </div>
        ) : !data ? (
          <p className="text-sm text-muted-foreground">Channel usage unavailable right now.</p>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {(data.channels || []).map((c) => {
                const Icon = CH_ICONS[c.key] || Plug;
                return (
                  <button
                    key={c.key}
                    type="button"
                    onClick={() => onOpenChannel && onOpenChannel(c.key, c.connected)}
                    data-testid={`channel-tile-${c.key}`}
                    className="group text-left rounded-xl border border-border/60 bg-card p-4 flex flex-col gap-3 transition-all hover:border-primary/40 hover:shadow-sm hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-primary/30"
                  >
                    {/* Header: icon + name (left) / status dot (right) */}
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2.5 min-w-0">
                        <span className={`h-9 w-9 rounded-lg flex items-center justify-center shrink-0 ${CH_ACCENT[c.key] || 'text-muted-foreground bg-muted'}`}>
                          <Icon className="h-4 w-4" />
                        </span>
                        <span className="text-sm font-semibold truncate">{c.label}</span>
                      </div>
                      <span className={`h-2 w-2 rounded-full shrink-0 ${c.connected ? 'bg-emerald-500' : 'bg-amber-500'}`}
                        title={c.connected ? 'Connected' : 'Not connected'} />
                    </div>

                    {/* Status line */}
                    {c.connected ? (
                      <p className="text-[11px] text-muted-foreground truncate" title={c.detail || ''}
                        data-testid={`channel-connected-${c.key}`}>
                        {c.detail || 'Connected'}
                      </p>
                    ) : (
                      <p className="text-[11px] font-medium text-amber-600 flex items-center gap-1"
                        data-testid={`channel-disconnected-${c.key}`}>
                        <AlertTriangle className="h-3 w-3 shrink-0" /> Not connected
                      </p>
                    )}

                    {/* Stats or connect CTA */}
                    {c.connected ? (
                      <div className="mt-auto pt-3 border-t border-border/40 space-y-2">
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <div className="text-base font-semibold leading-none" data-testid={`channel-planned-${c.key}`}>{c.planned_steps_pending}</div>
                            <div className="text-[10px] text-muted-foreground mt-1">in cadences · {c.planned_leads} leads</div>
                          </div>
                          <div>
                            <div className="text-base font-semibold leading-none" data-testid={`channel-executed-${c.key}`}>{c.executed_30d}</div>
                            <div className="text-[10px] text-muted-foreground mt-1">sent · 30 days</div>
                          </div>
                        </div>
                        {c.last_delivery && (
                          <div className={`flex items-center gap-1 text-[10px] font-medium ${c.last_delivery.ok ? 'text-emerald-600' : 'text-red-500'}`}
                            data-testid={`channel-delivery-${c.key}`}>
                            {c.last_delivery.ok ? <CheckCircle className="h-3 w-3 shrink-0" /> : <AlertTriangle className="h-3 w-3 shrink-0" />}
                            <span className="truncate">Last: {c.last_delivery.label}</span>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="mt-auto pt-3 border-t border-border/40 flex items-center justify-between text-primary text-xs font-medium">
                        <span data-testid={`channel-nudge-${c.key}`}>
                          {c.planned_steps_pending > 0
                            ? `Connect to unlock ${c.planned_steps_pending} step${c.planned_steps_pending === 1 ? '' : 's'}`
                            : `Connect ${c.label}`}
                        </span>
                        <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
            <p className="text-[11px] text-muted-foreground mt-3">
              {data.active_cadences} active/paused cadence{data.active_cadences === 1 ? '' : 's'}. Every step routes through
              this tenant's own connected channels automatically — email via your mailbox, calls via your Twilio/VoiceLink
              number, WhatsApp via your Meta number, LinkedIn as a manual Outbox task.
            </p>
          </>
        )}
      </CardContent>
    </Card>
  );
}


export default function IntegrationsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [providers, setProviders] = useState({});
  const [integrations, setIntegrations] = useState([]);
  const [setupProvider, setSetupProvider] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(null);
  const [emailIdentity, setEmailIdentity] = useState(null);
  const [mailWizardOpen, setMailWizardOpen] = useState(false);
  const [activeCategory, setActiveCategory] = useState('email');
  const [testEmailOpen, setTestEmailOpen] = useState(false);
  const [testEmailTo, setTestEmailTo] = useState('');
  const [sendingTest, setSendingTest] = useState(false);
  const [reconnectingMail, setReconnectingMail] = useState(false);
  // Type-to-confirm removal dialog: prevents an accidental one-click disconnect.
  const [removeTarget, setRemoveTarget] = useState(null); // { provider, name, isGmail }
  const [removeConfirmText, setRemoveConfirmText] = useState('');
  const [removing, setRemoving] = useState(false);

  // Tap a channel tile → jump to where it's connected/managed (best-practice deep link)
  const openChannel = useCallback((key) => {
    if (key === 'linkedin') { navigate('/linkedin-outbox'); return; }
    const tabFor = { email: 'email', call: 'voice', whatsapp: 'automation' };
    const cat = tabFor[key];
    if (cat) {
      setActiveCategory(cat);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [navigate]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [provRes, intRes, idRes] = await Promise.all([
        api.get('/api/tenant/integrations/providers').catch(() => ({ data: {} })),
        api.get('/api/tenant/integrations').catch(() => ({ data: [] })),
        api.get('/api/tenant/integrations/email/sending-identity').catch(() => ({ data: null })),
      ]);

      setProviders(provRes.data?.providers || {});
      setIntegrations(Array.isArray(intRes.data) ? intRes.data : []);
      setEmailIdentity(idRes.data || null);
    } catch (e) {
      console.error('Failed to load integrations:', e);
      toast.error('Failed to load integrations');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSave = async (provider, fields) => {
    setSaving(true);
    try {
      const body = { provider, is_enabled: true };
      if (fields.api_key) body.api_key = fields.api_key;
      if (fields.sender_email) body.sender_email = fields.sender_email;
      if (fields.api_secret) body.api_secret = fields.api_secret;
      if (fields.account_sid) body.account_sid = fields.account_sid;
      if (fields.phone_number) body.phone_number = fields.phone_number;
      // Exotel-specific fields
      if (fields.region) body.region = fields.region;
      if (fields.applet_id) body.applet_id = fields.applet_id;
      if (fields.elevenlabs_phone_id) body.elevenlabs_phone_id = fields.elevenlabs_phone_id;
      // SMTP-specific fields
      if (fields.smtp_host) body.smtp_host = fields.smtp_host;
      if (fields.smtp_port) body.smtp_port = parseInt(fields.smtp_port) || 465;
      if (fields.smtp_username) body.smtp_username = fields.smtp_username;
      if (fields.smtp_password) body.smtp_password = fields.smtp_password;
      if (fields.smtp_from_email) body.smtp_from_email = fields.smtp_from_email;
      if (fields.smtp_from_name) body.smtp_from_name = fields.smtp_from_name;
      // IMAP (inbound reply capture) — optional overrides
      if (fields.imap_host) body.imap_host = fields.imap_host;
      if (fields.imap_port) body.imap_port = parseInt(fields.imap_port) || 993;
      // Twilio country caller-ID pool: array of {country, number} → {US:"+1..",..}
      if (provider === 'twilio' && Array.isArray(fields._caller_pool)) {
        const pool = {};
        fields._caller_pool.forEach(r => {
          const c = (r.country || '').trim().toUpperCase();
          const n = (r.number || '').trim();
          if (c && n) pool[c] = n;
        });
        body.caller_id_pool = pool;
      }

      await api.post(`/api/tenant/integrations/${provider}`, body);
      toast.success(`${providers[provider]?.name || provider} connected successfully`);
      setSetupProvider(null);
      await handleTest(provider);
      await fetchData();
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error('Setup failed', { description: detail || 'Failed to save integration' });
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async (provider) => {
    setTesting(provider);
    try {
      const res = await api.post(`/api/tenant/integrations/${provider}/test`);
      if (res.data.success) {
        toast.success(`${providers[provider]?.name}: ${res.data.message}`);
      } else {
        toast.error(`${providers[provider]?.name}: ${res.data.message}`);
      }
      await fetchData();
    } catch (e) {
      toast.error('Test failed');
    } finally {
      setTesting(null);
    }
  };

  const openTestEmail = () => {
    setTestEmailTo(user?.email || '');
    setTestEmailOpen(true);
  };

  const sendTestEmail = async () => {
    const to = (testEmailTo || '').trim();
    if (!to || !to.includes('@')) { toast.error('Enter a valid recipient email'); return; }
    setSendingTest(true);
    try {
      const res = await api.post('/api/tenant/integrations/email/send-test', { to_email: to });
      if (res.data.success) {
        toast.success(res.data.message || `Test email sent to ${to}`);
        setTestEmailOpen(false);
      } else {
        toast.error(res.data.message || 'Could not send test email');
      }
    } catch (e) {
      toast.error('Test email failed', { description: e.response?.data?.detail || 'Please try again.' });
    } finally {
      setSendingTest(false);
    }
  };

  const handleReconnectMail = async () => {
    const provider = emailIdentity?.provider;
    if (!provider) return;
    // Gmail is OAuth-based — "reconnect" means re-authorising, so route it to
    // the guided wizard rather than a silent credential re-test.
    if (provider === 'gmail') {
      toast.info('Reconnect Gmail from the Connect Mail wizard (re-authorise your Google account).');
      setMailWizardOpen(true);
      return;
    }
    setReconnectingMail(true);
    try {
      const res = await api.post(`/api/tenant/integrations/${provider}/reconnect`);
      if (res.data.success) {
        toast.success(res.data.message || 'Reconnected');
      } else {
        toast.error('Reconnect needs attention', { description: res.data.message });
        // B3: saved credentials no longer work — open the guided wizard so the
        // user can re-enter their app password / fix the host, instead of being
        // stuck on a dead toast.
        setMailWizardOpen(true);
      }
      await fetchData();
    } catch (e) {
      toast.error('Reconnect failed', { description: e.response?.data?.detail || 'Please try again.' });
    } finally {
      setReconnectingMail(false);
    }
  };

  // Item 4: one-tap Zoho data-center fix — swap saved .com<->.in hosts and re-verify.
  const handleSwitchDc = async (targetDc) => {
    setReconnectingMail(true);
    try {
      const res = await api.post('/api/tenant/integrations/smtp/reconnect', { switch_dc: targetDc });
      if (res.data.success) {
        toast.success(res.data.message || `Switched to ${targetDc === 'in' ? 'India (.in)' : 'Global (.com)'} servers ✓`);
      } else {
        toast.error("Switch didn't verify", { description: res.data.message });
      }
      await fetchData();
    } catch (e) {
      toast.error('Switch failed', { description: e.response?.data?.detail || 'Please try again.' });
    } finally {
      setReconnectingMail(false);
    }
  };


  // Type-to-confirm removal: opens a dialog requiring the provider name to be
  // typed before the encrypted credentials are deleted (guards accidents).
  const requestDisconnect = (provider) => {
    setRemoveConfirmText('');
    // B2: confirm against a real identity (the connected email) for mail
    // providers, or a fixed keyword otherwise — never the display label, which
    // users can't guess and left the Remove button permanently disabled.
    const isMail = ['smtp', 'sendgrid', 'gmail'].includes(provider);
    const identity = isMail ? (emailIdentity?.from_email || '').trim() : '';
    setRemoveTarget({
      provider,
      name: providers[provider]?.name || provider,
      isGmail: provider === 'gmail',
      confirmValue: identity || 'REMOVE',
    });
  };

  const confirmRemove = async () => {
    if (!removeTarget) return;
    const { provider, name, isGmail } = removeTarget;
    setRemoving(true);
    try {
      if (isGmail) {
        await api.delete('/api/mailbox/disconnect');
      } else {
        await api.delete(`/api/tenant/integrations/${provider}`);
      }
      toast.success(`${name} disconnected`);
      setRemoveTarget(null);
      await fetchData();
    } catch (e) {
      toast.error('Disconnect failed', { description: e.response?.data?.detail });
    } finally {
      setRemoving(false);
    }
  };

  const handleDisconnect = (provider) => requestDisconnect(provider);

  // Gmail is connected via OAuth (mailbox_connections), NOT the BYOK
  // tenant_integrations store — so it must be disconnected through its own
  // endpoint. Both routes go through the same type-to-confirm dialog.
  const handleDisconnectMail = (provider) => requestDisconnect(provider);

  // Map integrations by provider
  const intByProvider = {};
  integrations.forEach(i => { intByProvider[i.provider] = i; });

  const connected = integrations.filter(i => i.is_configured).length;

  // Grid providers exclude email (owned by the Email & Inbox hub), grouped by category.
  const gridEntries = Object.entries(providers).filter(([id]) => !EMAIL_PROVIDER_IDS.includes(id));
  const byCat = (cat) => gridEntries.filter(([id]) => (PROVIDER_CATEGORY[id] || 'other') === cat);
  const renderProviderCard = ([id, prov]) => (
    <IntegrationCard
      key={id}
      provider={{ id, ...prov }}
      integration={intByProvider[id]}
      onSetup={setSetupProvider}
      onTest={handleTest}
      onSendTest={openTestEmail}
      onDisconnect={handleDisconnect}
      testing={testing}
    />
  );

  const renderExotelCard = () => {
    const it = intByProvider['exotel'] || {};
    const connected = !!it.is_configured;
    const ready = it.voice_ai_status === 'ready';
    const needsApplet = it.voice_ai_status === 'needs_applet_id' || (connected && !it.applet_id);
    return (
      <Card className="md:col-span-2 lg:col-span-3" data-testid="exotel-card">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg flex items-center justify-center text-emerald-500 bg-emerald-500/10">
                <Phone className="h-5 w-5" />
              </div>
              <div>
                <CardTitle className="text-base">Exotel — India Number (BYOK)</CardTitle>
                <CardDescription className="text-xs">
                  Connect your own Exotel account (TRAI-compliant India voice + click-to-call) for AI inbound &amp; outbound. Your keys are encrypted and never exposed.
                </CardDescription>
              </div>
            </div>
            {connected ? (
              ready ? (
                <Badge variant="outline" className="bg-green-500/5 border-green-500/30 text-green-600" data-testid="exotel-status">
                  <CheckCircle className="h-3 w-3 mr-1" />Voice AI ready
                </Badge>
              ) : (
                <Badge variant="outline" className="bg-amber-500/5 border-amber-500/30 text-amber-600" data-testid="exotel-status">
                  <AlertTriangle className="h-3 w-3 mr-1" />{needsApplet ? 'Needs Applet ID' : 'Connected'}
                </Badge>
              )
            ) : (
              <Badge variant="outline" className="text-muted-foreground" data-testid="exotel-status">Not Connected</Badge>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {connected && (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 p-3 rounded-lg bg-muted/30 border border-border/50 text-sm">
              <div>
                <div className="text-[11px] uppercase tracking-wide text-muted-foreground">ExoPhone</div>
                <div className="font-medium truncate">{it.phone_number || '—'}</div>
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Applet ID</div>
                <div className={`font-medium ${it.applet_id ? '' : 'text-amber-600'}`}>{it.applet_id || 'Not set'}</div>
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Voice AI</div>
                <div className={`font-medium ${ready ? 'text-green-600' : 'text-muted-foreground'}`}>{ready ? 'Ready' : 'Setup pending'}</div>
              </div>
            </div>
          )}
          <div className="flex flex-wrap items-center gap-2">
            <Button size="sm" variant={connected ? 'outline' : 'default'} onClick={() => setSetupProvider('exotel')} data-testid="exotel-setup-btn">
              <KeyRound className="h-4 w-4 mr-1.5" />{connected ? 'Update' : 'Connect Exotel'}
            </Button>
            {connected && (
              <Button size="sm" variant="ghost" onClick={() => handleTest('exotel')} disabled={testing === 'exotel'} data-testid="exotel-test-btn">
                {testing === 'exotel' ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <TestTube className="h-4 w-4 mr-1.5" />}Test
              </Button>
            )}
            {connected && (
              <Button size="sm" variant="ghost" className="text-red-600" onClick={() => handleDisconnect('exotel')} data-testid="exotel-disconnect-btn">
                <Trash2 className="h-4 w-4 mr-1.5" />Disconnect
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    );
  };

  const catConnected = (ids) => ids.filter((id) => intByProvider[id]?.is_configured).length;
  const CATEGORIES = [
    { id: 'email', label: 'Email & Inbox', count: emailIdentity?.connected ? 1 : 0, connected: emailIdentity?.connected ? 1 : 0 },
    { id: 'voice', label: 'Voice & SMS', count: 4 + byCat('voice').filter(([id]) => id !== 'twilio' && id !== 'exotel').length, connected: catConnected(byCat('voice').map(([id]) => id)) },
    { id: 'payments', label: 'Payments', count: byCat('payments').length, connected: catConnected(byCat('payments').map(([id]) => id)) },
    { id: 'automation', label: 'Automation & Social', count: 3 + byCat('automation').length, connected: catConnected(byCat('automation').map(([id]) => id)) },
    { id: 'calendar', label: 'Calendar & Scheduling', count: 1, connected: 0 },
  ];
  if (byCat('other').length > 0) {
    CATEGORIES.push({ id: 'other', label: 'Other', count: byCat('other').length, connected: catConnected(byCat('other').map(([id]) => id)) });
  }

  if (loading) {
    return (
      <div className="p-6 max-w-6xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <Plug className="h-6 w-6 text-primary" />
          <h1 className="text-2xl font-bold">Integrations</h1>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 py-6">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="p-5 rounded-lg border space-y-3">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-muted animate-pulse" />
                <div className="space-y-1 flex-1">
                  <div className="h-4 w-28 bg-muted rounded animate-pulse" />
                  <div className="h-3 w-20 bg-muted/40 rounded animate-pulse" />
                </div>
              </div>
              <div className="h-3 w-full bg-muted/40 rounded animate-pulse" />
              <div className="h-8 w-24 bg-muted/60 rounded animate-pulse" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="integrations-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
            <Plug className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-1.5">Integrations <HelpDot guide="connect-email" label="How to connect email & channels" /></h1>
            <p className="text-sm text-muted-foreground">
              Connect third-party services to supercharge your CRM
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Badge variant="outline" className="text-sm px-3 py-1" data-testid="connected-count-badge">
            {connected}/{Object.keys(providers).length} connected
          </Badge>
          <Button variant="outline" size="sm" onClick={fetchData} data-testid="refresh-integrations-btn">
            <RefreshCw className="h-4 w-4 mr-1" /> Refresh
          </Button>
        </div>
      </div>

      {/* Master-detail: left category nav + right relevant tools */}
      <div className="flex flex-col lg:flex-row gap-6" data-testid="integrations-layout">
        {/* LEFT NAV */}
        <div className="lg:w-60 shrink-0 space-y-1" data-testid="integrations-nav">
          {CATEGORIES.map((cat) => {
            const active = activeCategory === cat.id;
            return (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                data-testid={`nav-${cat.id}`}
                className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-sm transition-colors border ${
                  active ? 'bg-primary/10 text-foreground border-primary/30' : 'text-muted-foreground hover:bg-muted/50 border-transparent'}`}
              >
                <span className="font-medium">{cat.label}</span>
                <span className="flex items-center gap-1.5 shrink-0">
                  {cat.connected > 0 && <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" title="Connected" />}
                  <span className="text-[11px] text-muted-foreground">{cat.count}</span>
                  {active && <ChevronRight className="h-3.5 w-3.5 text-primary" />}
                </span>
              </button>
            );
          })}
          <div className="mt-4 rounded-lg border border-border bg-muted/20 p-3 flex gap-2">
            <Shield className="h-4 w-4 text-primary shrink-0 mt-0.5" />
            <p className="text-[11px] text-muted-foreground">
              BYOK — your keys are encrypted at rest and never exposed. Every integration has a guided setup and health checks.
            </p>
          </div>
        </div>

        {/* RIGHT CONTENT — relevant tools for the selected category */}
        <div className="flex-1 min-w-0" data-testid="integrations-content">
          <ChannelOverviewCard onOpenChannel={openChannel} />
          {activeCategory === 'email' && (
            <div data-testid="category-email">
              <MailHub
                emailIdentity={emailIdentity}
                smtpIntegration={intByProvider['smtp']}
                gmailConnected={emailIdentity?.provider === 'gmail'}
                onConnectMail={() => setMailWizardOpen(true)}
                onSendTest={openTestEmail}
                onReconnectMail={emailIdentity?.provider ? handleReconnectMail : null}
                reconnecting={reconnectingMail}
                onSwitchDc={emailIdentity?.provider === 'smtp' ? handleSwitchDc : null}
                onDisconnectMail={emailIdentity?.provider ? () => handleDisconnectMail(emailIdentity.provider) : null}
              />
            </div>
          )}
          {activeCategory === 'voice' && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="category-voice">
              <VoiceReadinessCard />
              <TwilioNumberCard />
              <VoiceLinkCard />
              {renderExotelCard()}
              {byCat('voice').filter(([id]) => id !== 'twilio' && id !== 'exotel').map(renderProviderCard)}
            </div>
          )}
          {activeCategory === 'payments' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="category-payments">
              {byCat('payments').length > 0 ? byCat('payments').map(renderProviderCard)
                : <p className="text-sm text-muted-foreground">No payment integrations available.</p>}
            </div>
          )}
          {activeCategory === 'automation' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="category-automation">
              <WebsiteFormCard />
              <ChatWidgetCard />
              <SocialChannelsCard />
              {byCat('automation').map(renderProviderCard)}
            </div>
          )}
          {activeCategory === 'calendar' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="category-calendar">
              <GoogleCalendarCard />
            </div>
          )}
          {activeCategory === 'other' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="category-other">
              {byCat('other').map(renderProviderCard)}
            </div>
          )}
        </div>
      </div>

      {/* Setup Wizard */}
      {setupProvider && (
        <SetupWizard
          provider={setupProvider}
          open={!!setupProvider}
          onClose={() => setSetupProvider(null)}
          onSave={handleSave}
          saving={saving}
          initialPool={(intByProvider[setupProvider] || {}).caller_id_pool}
        />
      )}

      {/* Connect Mail wizard (guided, plug-and-play) */}
      <ConnectMailWizard
        open={mailWizardOpen}
        onClose={() => setMailWizardOpen(false)}
        onComplete={fetchData}
      />

      {/* Type-to-confirm removal dialog — guards against accidental disconnects */}
      <Dialog open={!!removeTarget} onOpenChange={(o) => { if (!o) setRemoveTarget(null); }}>
        <DialogContent className="max-w-md" data-testid="remove-integration-dialog">
          <DialogHeader>
            <DialogTitle>Remove {removeTarget?.name}?</DialogTitle>
            <DialogDescription>
              This deletes your stored (encrypted) credentials for {removeTarget?.name}.
              {removeTarget?.isGmail
                ? ' Outbound and inbound email via Gmail will stop.'
                : ' Any email/automation using this integration will stop until you reconnect.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <label className="text-xs text-muted-foreground">
              Type <span className="font-semibold text-foreground">{removeTarget?.confirmValue}</span> to confirm
            </label>
            <Input
              value={removeConfirmText}
              onChange={(e) => setRemoveConfirmText(e.target.value)}
              placeholder={removeTarget?.confirmValue}
              autoFocus
              data-testid="remove-confirm-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRemoveTarget(null)} data-testid="remove-cancel-btn">
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={confirmRemove}
              disabled={removing || (removeConfirmText || '').trim().toLowerCase() !== (removeTarget?.confirmValue || '').trim().toLowerCase()}
              data-testid="remove-confirm-btn"
            >
              {removing ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Trash2 className="h-4 w-4 mr-1" />}
              Remove integration
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send Test Email Dialog */}
      <Dialog open={testEmailOpen} onOpenChange={setTestEmailOpen}>
        <DialogContent className="max-w-md" data-testid="send-test-email-dialog">
          <DialogHeader>
            <DialogTitle>Send a test email</DialogTitle>
            <DialogDescription>
              We&apos;ll send a test email through your active sending identity to confirm it&apos;s working.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <label className="text-xs text-muted-foreground">Recipient email</label>
            <Input
              type="email"
              value={testEmailTo}
              onChange={(e) => setTestEmailTo(e.target.value)}
              placeholder="you@company.com"
              data-testid="test-email-recipient-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTestEmailOpen(false)} data-testid="test-email-cancel-btn">
              Cancel
            </Button>
            <Button onClick={sendTestEmail} disabled={sendingTest} data-testid="test-email-send-btn">
              {sendingTest ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Mail className="h-4 w-4 mr-1" />}
              Send test
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
